// Icon from react-icons

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@uirack/components/ui/accordion';
import {
  AiFillDelete,
  AiOutlineClose,
  AiOutlineFileSearch,
  AiOutlineFullscreen,
  AiOutlineFullscreenExit,
} from 'react-icons/ai';
import { ConvoHistory, ConvoHistoryDisplayByChart, UserHistory, buildChartContext } from './utils';
import { describeAzureError, streamAzureResponse } from './azureResponses';
import { FaBars, FaCopy, FaFileDownload, FaTimes } from 'react-icons/fa';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import Loader, {
  LoaderBoxes,
} from '../modules/App/LayoutComponents/Loader/Loader';
import { PiSpeakerHighDuotone, PiSpeakerSimpleXBold } from 'react-icons/pi';
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { BASE_URI } from '../services/common/endpoints';
import { BsFillMicFill } from 'react-icons/bs';
import { ChatgptContext } from './context_chatgpt/ChatgptContext';
import { CiCircleInfo } from 'react-icons/ci';
import Dialog from '../common/Dialog';
import DropdownCheckbox from '../common/DropdownCheckbox';
import { FaUserCircle } from 'react-icons/fa';
import { FcDisclaimer } from 'react-icons/fc';
import { GoLinkExternal } from 'react-icons/go';
import { INSTAGRAM_TYPE, TIKTOK_TYPE, FACEBOOK_TYPE, YOUTUBE_TYPE } from '../common/constants';
import { LANGUAGES_LIST } from '../common/metadata';
import { MicListingComponent } from './context_chatgpt/MicListingComponent';
import Pill from '../modules/Components/Pill';
import Select from 'react-select';
import SelectFilterFilter from '../modules/SelectFilter/SelectFilterFilter';
import Tooltip from '../common/Tooltip/Tooltip';
import { UserProfileContext } from '../store/context/profile.context';
import dayjs from 'dayjs';
import { getToken } from '../services/common/authToken';
import pillsData from './prompt.json';
import { useNavigate } from 'react-router-dom';

/* eslint-disable no-undef */

const systemMessage = {
  role: 'assistant',
  content:
    'Summarize the provided data and create concise summaries or key points.',
};

let chartName = '';

// Same pattern as computeDateRange() in modules/Insights/InsightsPanelFrontend.jsx —
// prefer the real selected start/end dates when a custom range is active,
// otherwise derive them from the day count (end=today, start=today-days).
// Used to send day_count/start_date/end_date on every saved chat record so
// the backend can retrieve/filter history by the date range it actually
// covered, instead of the free-text `interval` string that was sent before.
const isoDateChatgptApi = (d) => d.toISOString().slice(0, 10);
function computeDateRangeChatgptApi(searchObject, days) {
  if (searchObject?.updateStartDays && searchObject?.updateEndDays) {
    return { start_date: searchObject.updateStartDays, end_date: searchObject.updateEndDays };
  }
  if (searchObject?.startDate && searchObject?.endDate) {
    return { start_date: searchObject.startDate, end_date: searchObject.endDate };
  }
  if (!days) return { start_date: null, end_date: null };
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - Number(days));
  return { start_date: isoDateChatgptApi(start), end_date: isoDateChatgptApi(end) };
}

// Top Insights Dashboard KPI cards (Sentiment Timeline, Posts/Comments by
// Language, etc.) track the FULL multi-platform selection — which platforms
// are active, and each platform's own subtype pills — as several sibling
// state vars on the page (currentMediaChannelMulti + tiktokSubTabMulti /
// instagramSubTabMulti / youtubeSubTabMulti), injected into `searchObject`
// the same way TotalMentions.jsx already does. Individual chart components
// were instead hardcoding a single last-active platform/subtype into the
// `socialMediaType`/`subtype` props (e.g. always "instagram"/
// "manual_hashtags" even with TikTok + YouTube also active), so multi-
// platform runs were silently saved/queried as if only one platform mattered.
// These derive the real list/subtype from SearchObject when available,
// falling back to the old single-value props otherwise.
// Every Top Insights Dashboard chart's `searchObject` is built by
// queryDetailsToSearchObject() (Insights/Transformer.Insights.js) as
// `{...queryDetails.channels, ...queryDetails.formData}` — so
// searchObject.instagram / .tiktok / .youtube / etc. are top-level keys
// present on literally every chart's searchObject, since they're the same
// fields useStatsSearchCustomHook itself reads to know which platforms are
// active. Far more reliable than currentMediaChannelMulti/multiSocialMedia,
// which many individual chart components never get around to injecting.
const KNOWN_PLATFORM_KEYS = [
  'instagram', 'tiktok', 'youtube', 'facebook', 'twitter', 'reddit',
  'articles', 'linkedin', 'linkedinPublicPage',
];

function computeMultiPlatformNames(searchObject, fallbackSocialMediaType) {
  const fromSearchObjectKeys = KNOWN_PLATFORM_KEYS.filter((k) => searchObject?.[k]);
  if (fromSearchObjectKeys.length > 0) return fromSearchObjectKeys;

  const multiObj = searchObject?.currentMediaChannelMulti;
  // Some callers inject the plain `currentMediaChannelMulti` ARRAY prop
  // (e.g. ['instagram']) here instead of the `multiSocialMedia` OBJECT
  // (e.g. {instagram: 'nestle'}) the rest of this function expects.
  // Object.keys() on an array returns numeric index strings ("0", "1"...),
  // not platform names — confirmed live as `social_media_name=0`. Handle
  // the array shape directly instead of falling into that trap.
  if (Array.isArray(multiObj) && multiObj.length > 0) {
    return multiObj.filter(Boolean);
  }
  if (multiObj && typeof multiObj === 'object' && Object.keys(multiObj).length > 0) {
    return Object.keys(multiObj);
  }
  if (Array.isArray(fallbackSocialMediaType)) return fallbackSocialMediaType.filter(Boolean);
  if (fallbackSocialMediaType) return [fallbackSocialMediaType];
  return [];
}

// Canonical subtype codes per platform (same constants used elsewhere in
// the app for these tab values) — used whenever we can't pin down exactly
// which subtype(s) are active, so the backend gets every real code that
// could apply to an active platform instead of a vague "all" marker.
const PLATFORM_SUBTYPE_VALUES = {
  instagram: INSTAGRAM_TYPE,
  tiktok: TIKTOK_TYPE,
  facebook: FACEBOOK_TYPE,
  youtube: YOUTUBE_TYPE,
};

function allSubtypeValuesForPlatforms(platformNames) {
  if (!Array.isArray(platformNames)) return [];
  const values = platformNames.flatMap(
    (p) => PLATFORM_SUBTYPE_VALUES[String(p).toLowerCase()] || [],
  );
  return [...new Set(values)];
}

function computeMultiPlatformSubtype(searchObject, fallbackSubtype, platformNames) {
  const perPlatformSubtypes = [
    searchObject?.tiktokSubTabMulti,
    searchObject?.instagramSubTabMulti,
    searchObject?.youtubeSubTabMulti,
  ].filter((arr) => Array.isArray(arr) && arr.length > 0);
  if (perPlatformSubtypes.length > 0) {
    const distinctValues = [...new Set(perPlatformSubtypes.flat())];
    // More than one platform contributing subtypes (or one platform with
    // more than one subtype pill active) — no single string represents
    // that correctly, so send every canonical subtype code for the active
    // platforms instead of picking just one.
    if (perPlatformSubtypes.length > 1 || distinctValues.length > 1) {
      const allValues = allSubtypeValuesForPlatforms(platformNames);
      return allValues.length > 0 ? allValues : distinctValues;
    }
    return distinctValues[0];
  }
  // No granular per-platform subtype data reached this component (several
  // "Ask Omnio AI Agent" trigger points don't thread tiktokSubTabMulti/
  // instagramSubTabMulti/youtubeSubTabMulti down at all) — but if we can
  // still see which platform(s) are active (via platformNames, which has
  // its own fallback chain), send every canonical subtype code for them —
  // a single hardcoded/guessed string is likely wrong for at least one.
  const allValues = allSubtypeValuesForPlatforms(platformNames);
  if (allValues.length > 0) return allValues;
  return fallbackSubtype;
}

const Chatgpt_API = ({ setIsFullwidth = () => { } }) => {
  const {
    setSidebarStatus,
    sidebarStatus,
    ChatgptData,
    convoId,
    setconvoId,
    PillsName,
    socialMediaType,
    mainSearchTerm,
    subtype,
    SearchObject,
    isFullWidth,
    setIsFullWidth,
    currentMediaChannelMulti,
    ...props
  } = useContext(ChatgptContext);

  const [lang, setlang] = useState('');
  const [toggleMic, settoggleMic] = useState(false);
  // listning with user mic
  const [isMicON, setisMicON] = useState(false);
  const [toggleScreen, settoggleScreen] = useState(false);
  const [modal, setmodal] = useState(false);
  const [promptInitialData, setPromptInitialData] = useState([]);
  const [promptApiData, setPromptApiData] = useState([]);
  const [promptApiDataDefalt, setPromptApiDataDefalt] = useState([]);

  const [summary, setSummary] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [topPillsName, setTopPillsName] = useState();
  const [isPromptData, setIsPromptData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [apiPromptDataCustom, setApiPromptData] = useState([]);
  const [isClickedAddNew, setIsClickedAddNew] = useState(false);

  const [userInputShow, setUserInputShow] = useState(false);
  const [apikeyValue, setApiKey] = useState('');
  const [selectInstruction, setSelectInstruction] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const [newPromptName, setNewPromptName] = useState('');
  const [newPromptValue, setNewPromptValue] = useState(prompt);
  const [updateDetails, setUpdateDetails] = useState('');
  const [isImmersiveView, setIsImmersiveView] = useState(true);
  const [historyPillsValue, setHIstoryPillsValue] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [initialData, setInitialData] = useState([]);
  const [toggleIcon, setToggleIcon] = useState(true);
  const [toggleHistory, setToggleHistory] = useState(true);
  const [toggleField, setToggleField] = useState(true);
  const [promptPillsArray, setPromptPillsArray] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  // const [itemsToShow, setItemsToShow] = useState(3);

  const [showModal, setshowModal] = useState(false);
  const [showModalSubscribe, setShowModalSubscribe] = useState(false);
  const [showModalMessage, setshowModalMessage] = useState('');
  const [ModalTitle, setModalTitle] = useState('');
  const [selectedModel, setSelectedModel] = useState('router_model');
  const [fieldName, setFieldName] = useState([]);
  const [fieldNameDropdown, setFieldNameDropdown] = useState([]);
  const [dateStartEnd, setDateStartEnd] = useState(0);

  const [countAPIcall, setCountAPIcall] = useState(0);

  // const profileContext = useContext(UserProfileContext);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  // Default (collapsed) and full (expanded) field sets for the current title,
  // used by the "More / Fewer fields" toggle in the Select Data Fields row.
  const [defaultTitleFields, setDefaultTitleFields] = useState([]);
  const [fullTitleFields, setFullTitleFields] = useState([]);

  const profileContext = useContext(UserProfileContext);
  const user = profileContext?.profileInfo?.accountname;

  const isSubscriptionActive = profileContext?.profileInfo?.subscription;
  const totalAvailableApiCount =
    profileContext?.profileInfo?.GenAiInsights?.limit;

  const totalAvailableToken =
    profileContext?.profileInfo?.GenAiInsights?.token_limit || 0;

  const [apiCount, setApiCount] = useState(0);
  const [tokenUsed, setTokenUsed] = useState();
  const [timer, setTimer] = useState(30);

  const useMouseDelta = () => {
    const [dragging, setDragging] = useState(false);
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;

    let initialWidth = viewportWidth * 0.60;
    const [result, setResult] = useState(initialWidth);
    const [currentWidth, setCurrentWidth] = useState(initialWidth);

    const origin = useRef(initialWidth);

    const setDefault = (value) => {
      setResult(value || initialWidth);
      setCurrentWidth(value || initialWidth);
      origin.current = value || initialWidth;
    };

    const handleMouseDown = useCallback((e) => {
      const elementId = (e.target || e.srcElement).id;
      origin.current = e.clientX;
      if (elementId === 'sliderChatgpt') {
        e.preventDefault();
        setDragging(true);
      }
    }, []);

    const handleMouseUp = useCallback(
      (e) => {
        setDragging(false);
        setCurrentWidth(result);
      },
      [result],
    );

    const handleMouseMove = useCallback(
      (e) => {
        if (!dragging) {
          return;
        }
        setResult(() => currentWidth - e.clientX + origin.current);
      },
      [currentWidth, dragging],
    );

    useEffect(() => {
      window.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('click', handleMouseUp);

      return () => {
        window.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }, [dragging, handleMouseDown, handleMouseUp, handleMouseMove]);

    return { result, setDefault };
  };

  const { result, setDefault } = useMouseDelta();

  const scrollContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [summary, isLoading, isAiLoading]);

  useEffect(() => {
    if (typeof setIsFullwidth === 'function') {
      const width = window.innerWidth || document.documentElement.clientWidth;
      if (result < width && sidebarStatus) {
        setIsFullwidth(false);
      } else if (sidebarStatus) {
        setIsFullwidth(true);
      } else {
        setIsFullwidth(false);
      }
    }
  }, [result, sidebarStatus]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // toggle speaker
  const [isListing, setisListing] = useState(false);
  // text-to-speak

  const navigate = useNavigate();
  const label =
    mainSearchTerm +
    ': ' +
    socialMediaType +
    ' - ' +
    ChatgptData?.title +
    ' - ' +
    SearchObject?.days || 10 + ' days' + ' - ' + topPillsName;

  useEffect(() => {
    setPrompt(process.env.REACT_APP_CHATGPT_SUMMARIZE_PROMPT);
    return () => {
      setconvoId('');
    };
  }, []);

  // useEffect(() => {
  //   setSummary([]);
  //   setTopPillsName('Overall Summary');
  //     setPrompt(selectedOption.value);
  // }, [socialMediaType, ChatgptData, sidebarStatus]);

  const sourceData = {
    mx_groq: 'gr',
    mx_groq_llama: 'grl',
    AdvancedAI: 'ad',
    'mixt-56b': 'm56',
    internlm: 'in',
    qwen: 'qw',
    'mixt-large': 'ml',
    Claude: 'cl',
    llama3_gradient: 'lm',
    llama3_gradient_new: 'lmg',
    'openai-azure': 'ag',
    llama70b: 'lm70b',
    router_model: "router_model",
  };

  const [historyData, setHistoryData] = useState([]);

  const [showAll, setShowAll] = useState(false);
  // Chat history always fetches every saved period (see day_count/start_date/
  // end_date in GetAndSetResponse's RequestBody) — this filters client-side
  // so switching periods never hides runs saved under a different range.
  const [historyPeriodFilter, setHistoryPeriodFilter] = useState('all');
  const filteredHistoryData =
    historyPeriodFilter === 'all'
      ? historyData
      : historyData.filter((h) => String(h.dayCount) === historyPeriodFilter);
  const itemsToShow = showAll ? filteredHistoryData : filteredHistoryData.slice(0, 3);

  // Account-wide token total (GET /api/stats/userHistory?task=token) — this
  // is auth-scoped to whoever's currently logged in, same endpoint the
  // Token Usage admin page uses. Shown alongside the per-prompt totals
  // already visible in Chat History above.
  const [accountTokenTotal, setAccountTokenTotal] = useState(null);
  const [accountTokenLoading, setAccountTokenLoading] = useState(false);
  const fetchAccountTokenTotal = async () => {
    setAccountTokenLoading(true);
    try {
      const res = await UserHistory({ task: 'token' });
      const body = res?.data ?? res;
      setAccountTokenTotal(body?.total_tokens_by_user ?? null);
    } catch (e) {
      // Non-fatal — this is a supplementary display, not core chat functionality.
    } finally {
      setAccountTokenLoading(false);
    }
  };
  // Same auth-readiness race as getTokonCount() below (see its comment) —
  // this fired unconditionally on mount via UserHistory(), which goes
  // through axiosInstance's getToken(), and lost the race against
  // AppBase's async token fetch just as often.
  // Also gated on sidebarStatus: this whole component stays mounted in
  // AppBase for the entire session (toggled via a CSS 'hidden' class, not
  // conditional rendering — see the sidenav className below), so firing on
  // mount meant every page load paid for this fetch even if the user never
  // opened the AI panel. Wait for the panel to actually open instead.
  const hasFetchedAccountTokenTotalRef = useRef(false);
  useEffect(() => {
    if (hasFetchedAccountTokenTotalRef.current) return;
    if (!profileContext?.profileInfo || !sidebarStatus) return;
    hasFetchedAccountTokenTotalRef.current = true;
    fetchAccountTokenTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileContext?.profileInfo, sidebarStatus]);

  const GetAndSetResponse = async () => {
    setIsLoading(true);
    // ;
    try {
      // const data = await UserHistory();

      const historyDayCount = getDayCount();
      const historyDateRange = computeDateRangeChatgptApi(SearchObject, historyDayCount);
      const historySocialMediaNames = computeMultiPlatformNames(SearchObject, socialMediaType);
      const RequestBody = {
        social_media_pills_name: PillsName || ChatgptData?.title,
        social_media_name: historySocialMediaNames.join(','),
        handler_name: mainSearchTerm.toLowerCase(),
        // Deliberately NOT filtering history by subtype (unlike the save
        // below): the backend appears to exact-match this field, and the
        // canonical-array format is new — history records saved before this
        // change (or with a different platform-subtype mix) use a different
        // string and would silently vanish from a subtype-filtered query.
        // Same reasoning as day_count/dates being handled client-side in
        // fetchReportHistory() for Insights Report — history should show
        // everything, not hide runs that don't match today's exact filter.
        source: sourceData?.[selectedModel],
        day_count: historyDayCount,
        start_date: historyDateRange.start_date,
        end_date: historyDateRange.end_date,
      };

      if (mainSearchTerm.toLowerCase()) {
        const data = await ConvoHistoryDisplayByChart(RequestBody);
        // ;

        if (data?.data?.length) {
          setInitialData(data);
          //
          // ;
          let arr = [];
          let pillsHistory = [];
          const dateArray = [];
          data?.data?.forEach((item) => {
            const obj = {
              chatId: item.chatId,
              conversationId: item.conversationId,
              date: item.date,
              chatName: item.chatName,
              usage: item.usage || null,
              dayCount: item.day_count || null,
              startDate: item.start_date || null,
              endDate: item.end_date || null,
            };
            pillsHistory.push(obj);

            if (!dateArray.includes(item.date.split('T')[0])) {
              dateArray.push(item.date.split('T')[0]);
            }

            let currentUserMessage = null;
            // item.messages.map((message) => {
            //   if (message.role == "user") {
            //     currentUserMessage = { user: message.content, content: null };
            //   } else if (message.role == "assistant" && currentUserMessage) {
            //     currentUserMessage.content = message.content;
            //     arr.push(currentUserMessage);
            //     currentUserMessage = null;
            //   }
            // });
          });

          setSummary(arr);
          setHIstoryPillsValue(dateArray);
          setHistoryData(pillsHistory);
          // historyPills()
          // setresponse(arr); // sets response
          // ShowChat(arr[0]); //displays index 0 data from response.

          setIsLoading(false); // after getting response makes loader false.
          document.getElementById('scrollIntoView').scrollIntoView(); // scrolls chat page to the bottom so that user can see latest chat.
        } else {
          setSummary([]);
          setHIstoryPillsValue([]);
          // Was missing — an empty response left whatever historyData was
          // last set for a DIFFERENT chart/company still on screen, showing
          // stale "history" that doesn't belong to the current selection.
          setHistoryData([]);
          setInitialData([]);
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  // Refreshes only the Chat History sidebar list (historyData/
  // historyPillsValue/initialData) after a new message saves successfully.
  // Deliberately does NOT touch `summary`/`isLoading` — GetAndSetResponse()
  // above sets `summary` from a loop that's fully commented out (always
  // empty), so calling it right after a send would instantly wipe the
  // response the user just received. This mirrors the same fetch, minus
  // that unsafe side effect, so the sidebar reflects the just-saved
  // conversation without clearing the visible chat.
  const refreshHistorySidebar = async () => {
    try {
      const historyDayCount = getDayCount();
      const historyDateRange = computeDateRangeChatgptApi(SearchObject, historyDayCount);
      const historySocialMediaNames = computeMultiPlatformNames(SearchObject, socialMediaType);
      const RequestBody = {
        social_media_pills_name: PillsName || ChatgptData?.title,
        social_media_name: historySocialMediaNames.join(','),
        handler_name: mainSearchTerm.toLowerCase(),
        source: sourceData?.[selectedModel],
        day_count: historyDayCount,
        start_date: historyDateRange.start_date,
        end_date: historyDateRange.end_date,
      };
      if (!mainSearchTerm.toLowerCase()) return;
      const data = await ConvoHistoryDisplayByChart(RequestBody);
      if (data?.data?.length) {
        setInitialData(data);
        const pillsHistory = [];
        const dateArray = [];
        data.data.forEach((item) => {
          pillsHistory.push({
            chatId: item.chatId,
            conversationId: item.conversationId,
            date: item.date,
            chatName: item.chatName,
            usage: item.usage || null,
            dayCount: item.day_count || null,
            startDate: item.start_date || null,
            endDate: item.end_date || null,
          });
          if (!dateArray.includes(item.date.split('T')[0])) {
            dateArray.push(item.date.split('T')[0]);
          }
        });
        setHIstoryPillsValue(dateArray);
        setHistoryData(pillsHistory);
      } else {
        setHIstoryPillsValue([]);
        setHistoryData([]);
        setInitialData([]);
      }
    } catch (error) {
      // Non-fatal — this only refreshes the sidebar list.
    }
  };

  const handleSummarizeBtn = () => {
    setPrompt(`${process.env.REACT_APP_CHATGPT_SUMMARIZE_PROMPT}`);
    setSidebarStatus(false);
    setconvoId('');
    chartName = '';
  };

  const fetchData = async () => {
    const data = {
      messages: [
        { role: 'system', content: 'You are a helpful assistant.' },
        { role: 'user', content: 'What is good about Wuhan?' },
      ],
      // max_tokens: 500,
      // temperature: 0.9,
      // stream: true,
    };

    const url =
      'https://Mistral-large-haix-v1-serverless.francecentral.inference.ai.azure.com/v1/chat/completions';
    const apiKey = 'oJZ82DuAQljIjLTsXXeROfEanDKRKNT7';

    if (!apiKey) {
      throw new Error('API Key is missing');
    }

    const headers = {
      'Content-Type': 'application/json',
      'api-key': apiKey,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
    } catch (error) {
      console.error('There was an error!', error);
    }
  };

  async function feedToChatGPT(theJsonData, { forceRefresh = false } = {}) {
    // scrollToBottom();
    scrollToTop();
    const apiRequestBody = {
      model: 'gpt-3.5-turbo',
      messages: [
        systemMessage,
        {
          role: 'user',
          content: `${lang
            ? ` write the output in ${lang} script language only (do not use english unless specified ) for this prompt : ${prompt}`
            : prompt
            } \n. ${JSON.stringify(filteredData)}`,
        },
      ],
    };

    const newApiData = {
      prompt: `${lang
        ? `write the output in ${lang} script language only (do not use English unless specified) for this prompt: ${prompt}`
        : prompt
        }`,
      content: `${JSON.stringify(filteredData)}`,
      // content: theJsonData,
    };
    const newApiDataNewModel = {
      query: `${lang
        ? `write the output in ${lang} script language only (do not use English unless specified) for this prompt: ${prompt}`
        : prompt
        }`,
      contents: `${JSON.stringify(filteredData)}`,
      // content: theJsonData,
    };

    if (!chartName) {
      chartName = prompt;
    }

    // if (selectedModel == 'AdvancedAI') {
    //   // try {
    //   //   // https://api.openai.com/v1/chat/completions
    //   //   await fetch('https://haixgenai.com/generate/', {
    //   //     method: 'POST',
    //   //     headers: {
    //   //       'Content-Type': 'application/json',
    //   //     },
    //   //     body: `${JSON.stringify(newApiData)}`,
    //   //   })
    //   //     .then((data) => {
    //   //       return data.json();
    //   //     })
    //   //     .then((data) => {
    //   //       if (data?.choices) {
    //   //         setSummary((prev) => [
    //   //           ...prev,
    //   //           {
    //   //             user: prompt,
    //   //             content: data?.choices[0]?.message?.content,
    //   //             token: data?.usage?.total_tokens,
    //   //           },
    //   //         ]);
    //   //       } else {
    //   //         setModalTitle('Error : 403');
    //   //         setshowModalMessage(`Please refresh and Login if needed`);
    //   //         setshowModal(true);
    //   //         // setTimeout(() => {
    //   //         //   setshowModal(false);
    //   //         // }, 10000);
    //   //       }
    //   //       const RequestBody = {
    //   //         _id: convoId,
    //   //         chatId: data?.id,
    //   //         chatName: chartName,
    //   //         sourceAPI: JSON.stringify(SearchObject),
    //   //         socialMediaSubType: subtype,
    //   //         conversationId: convoId,
    //   //         promptName: topPillsName,
    //   //         createdTime: data?.created,
    //   //         socialMediaType: Array.isArray(socialMediaType)
    //   //           ? socialMediaType // Convert array to comma-separated string
    //   //           : [socialMediaType],
    //   //         handlerUsed: mainSearchTerm.toLowerCase(),
    //   //         interval: SearchObject?.interval,
    //   //         socialMediaPillsName: PillsName || ChatgptData?.title,
    //   //         messages: [
    //   //           { role: 'user', content: prompt },
    //   //           { role: 'assistant', content: data.choices[0].message.content },
    //   //         ],
    //   //         usage: data?.usage,
    //   //         source: 'ad',
    //   //       };
    //   //       ConvoHistory(RequestBody)
    //   //         .then((res) => {})
    //   //         .catch((error) => {});
    //   //       setIsLoading(false);
    //   //     });
    //   // } catch (error) {
    //   //   setIsLoading(false);
    //   // }
    // }

    if (selectedModel == 'mx_groq' && (timer == 30 || timer <= 1)) {
      try {
        // https://api.openai.com/v1/chat/completions
        const response = await fetch(`${BASE_URI}/api/groq_mistral_model/v5`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(newApiData),
        });

        // Check if response is successful (status code 2xx)
        if (response.ok) {
          setIsLoading(false);

          const data = await response.json();
          if (Number(data?.usage?.total_tokens) >= 25000) {
            const countdown = setInterval(() => {
              let timeLimit = 30;
              setTimer((timeLimit) => {
                if (timeLimit <= 1) {
                  // Time's up
                  clearInterval(countdown);
                  return 0;
                } else {
                  // Decrease timer
                  return timeLimit - 1;
                }
              });
            }, 1000);
            setTimer(30);
          }

          if (data?.choices) {
            setSummary((prev) => [
              ...prev,
              {
                user: prompt,
                content: data?.choices[0]?.message?.content,
                token: data?.usage?.total_tokens,
              },
            ]);

            const RequestBody = {
              _id: convoId,
              chatId: data?.id,
              chatName: chartName,
              sourceAPI: JSON.stringify(SearchObject),
              socialMediaSubType: subtype,
              conversationId: convoId,
              promptName: topPillsName,
              createdTime: data?.created,
              ...buildChartContext({
                handlerUsed: mainSearchTerm,
                socialMediaType,
                searchObject: SearchObject,
              }),
              socialMediaPillsName: PillsName || ChatgptData?.title,
              messages: [
                { role: 'user', content: prompt },
                {
                  role: 'assistant',
                  content: data.choices[0].message.content,
                },
              ],
              usage: data?.usage,
              source: 'gr',
            };

            ConvoHistory(RequestBody)
              .then((res) => {
                // GetAndSetResponse();
              })
              .catch((error) => { });

            setIsLoading(false);
          } else if (data?.message) {
            setModalTitle('Error : 403');
            setshowModalMessage(
              'Token Limit Exceeded, Please try again after a minute',
            );
            setshowModal(true);
          } else {
            setModalTitle('Error : 500');
            setshowModalMessage(
              `Token Limit Exceeded, Please try again after a minute`,
            );
            setshowModal(true);
          }
        } else {
          setIsLoading(false);
          // Handle non-successful response (status code other than 2xx)
          setModalTitle(`Error: ${response.status}`);
          setshowModalMessage(
            `Token Limit Exceeded, Please try again after a minute`,
          );
          setshowModal(true);
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error('Error:', error);
        setModalTitle('Error');
        setshowModalMessage(
          'Token Limit Exceeded, Please try again after a minute',
        );
        setshowModal(true);
        setIsLoading(false);
      }
      setIsLoading(false);
    }

    if (selectedModel == 'llama70b' && (timer == 30 || timer <= 1)) {
      try {
        // https://api.openai.com/v1/chat/completions
        const response = await fetch(`${BASE_URI}/api/llama70b/v5?`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(newApiData),
        });

        // Check if response is successful (status code 2xx)
        if (response.ok) {
          setIsLoading(false);

          const data = await response.json();
          if (Number(data?.usage?.total_tokens) >= 25000) {
            const countdown = setInterval(() => {
              let timeLimit = 30;
              setTimer((timeLimit) => {
                if (timeLimit <= 1) {
                  // Time's up
                  clearInterval(countdown);
                  return 0;
                } else {
                  // Decrease timer
                  return timeLimit - 1;
                }
              });
            }, 1000);
            setTimer(30);
          }

          if (data?.choices) {
            setSummary((prev) => [
              ...prev,
              {
                user: prompt,
                content: data?.choices[0]?.message?.content,
                token: data?.usage?.total_tokens,
              },
            ]);

            const RequestBody = {
              _id: convoId,
              chatId: data?.id,
              chatName: chartName,
              sourceAPI: JSON.stringify(SearchObject),
              socialMediaSubType: subtype,
              conversationId: convoId,
              promptName: topPillsName,
              createdTime: data?.created,
              ...buildChartContext({
                handlerUsed: mainSearchTerm,
                socialMediaType,
                searchObject: SearchObject,
              }),
              socialMediaPillsName: PillsName || ChatgptData?.title,
              messages: [
                { role: 'user', content: prompt },
                {
                  role: 'assistant',
                  content: data.choices[0].message.content,
                },
              ],
              usage: data?.usage,
              source: 'lm70b',
            };

            ConvoHistory(RequestBody)
              .then((res) => {
                // GetAndSetResponse();
              })
              .catch((error) => { });

            setIsLoading(false);
          } else if (data?.message) {
            setModalTitle('Error : 403');
            setshowModalMessage(
              'Token Limit Exceeded, Please try again after a minute',
            );
            setshowModal(true);
          } else {
            setModalTitle('Error : 500');
            setshowModalMessage(
              `Token Limit Exceeded, Please try again after a minute`,
            );
            setshowModal(true);
          }
        } else {
          setIsLoading(false);
          // Handle non-successful response (status code other than 2xx)
          setModalTitle(`Error: ${response.status}`);
          setshowModalMessage(
            `Token Limit Exceeded, Please try again after a minute`,
          );
          setshowModal(true);
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error('Error:', error);
        setModalTitle('Error');
        setshowModalMessage(
          'Token Limit Exceeded, Please try again after a minute',
        );
        setshowModal(true);
        setIsLoading(false);
      }
      setIsLoading(false);
    }
    if (selectedModel == 'router_model' && (timer == 30 || timer <= 1)) {
      try {
        // Computed before the fetch (not just before the save) so the
        // request itself can go through /api/router_model_cached/v5 -
        // these are exactly the fields that endpoint matches a stored
        // answer on, so the request and the save below must build them
        // identically.
        const routerModelDayCount = getDayCount() || null;
        const routerModelDateRange = computeDateRangeChatgptApi(SearchObject, routerModelDayCount);
        const routerModelSocialMediaNames = computeMultiPlatformNames(SearchObject, socialMediaType);

        // Chart agents only: /api/router_model_cached/v5 is a drop-in
        // replacement for /api/router_model/v5 that returns a stored
        // answer (zero tokens) when the same chart/filters were already
        // asked about recently.
        const response = await fetch(`${BASE_URI}/api/router_model_cached/v5`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            ...newApiData,
            socialMediaPillsName: PillsName || ChatgptData?.title,
            handlerUsed: mainSearchTerm.toLowerCase(),
            socialMediaType: routerModelSocialMediaNames,
            socialMediaSubType: computeMultiPlatformSubtype(SearchObject, subtype, routerModelSocialMediaNames),
            interval: SearchObject?.interval,
            day_count: routerModelDayCount,
            start_date: routerModelDateRange.start_date,
            end_date: routerModelDateRange.end_date,
            forceRefresh,
          }),
        });

        // Check if response is successful (status code 2xx)
        if (response.ok) {
          setIsLoading(false);

          const data = await response.json();
          if (Number(data?.usage?.total_tokens) >= 25000) {
            const countdown = setInterval(() => {
              let timeLimit = 30;
              setTimer((timeLimit) => {
                if (timeLimit <= 1) {
                  // Time's up
                  clearInterval(countdown);
                  return 0;
                } else {
                  // Decrease timer
                  return timeLimit - 1;
                }
              });
            }, 1000);
            setTimer(30);
          }

          if (data?.choices) {
            setSummary((prev) => [
              ...prev,
              {
                user: prompt,
                content: data?.choices[0]?.message?.content,
                token: data?.usage?.total_tokens,
                dayCount: routerModelDayCount,
                startDate: routerModelDateRange.start_date,
                endDate: routerModelDateRange.end_date,
                cached: data?.cached,
                savedAt: data?.cachedAt,
              },
            ]);
            const RequestBody = {
              _id: convoId,
              chatId: data?.id,
              chatName: chartName,
              sourceAPI: JSON.stringify(SearchObject),
              socialMediaSubType: computeMultiPlatformSubtype(SearchObject, subtype, routerModelSocialMediaNames),
              conversationId: convoId,
              promptName: topPillsName,
              createdTime: data?.created,
              socialMediaType: routerModelSocialMediaNames,
              handlerUsed: mainSearchTerm.toLowerCase(),
              interval: SearchObject?.interval,
              day_count: routerModelDayCount,
              start_date: routerModelDateRange.start_date,
              end_date: routerModelDateRange.end_date,
              socialMediaPillsName: PillsName || ChatgptData?.title,
              messages: [
                { role: 'user', content: prompt },
                {
                  role: 'assistant',
                  content: data.choices[0].message.content,
                },
              ],
              usage: data?.usage,
              source: 'router_model',
            };

            ConvoHistory(RequestBody)
              .then((res) => {
                // Was commented out — the Chat History sidebar never
                // refreshed after a new message saved, so it kept showing
                // whichever conversation was fetched at mount time (stale
                // chatName, missing newer entries) until a manual refresh.
                // Uses the isolated refresh (not GetAndSetResponse) so it
                // doesn't also wipe the just-received response from view.
                refreshHistorySidebar();
              })
              .catch((error) => { });

            setIsLoading(false);
          } else if (data?.message) {
            setModalTitle('Error : 403');
            setshowModalMessage(
              'Token Limit Exceeded, Please try again after a minute',
            );
            setshowModal(true);
          } else {
            setModalTitle('Error : 500');
            setshowModalMessage(
              `Token Limit Exceeded, Please try again after a minute`,
            );
            setshowModal(true);
          }
        } else {
          setIsLoading(false);
          // Handle non-successful response (status code other than 2xx)
          setModalTitle(`Error: ${response.status}`);
          setshowModalMessage(
            `Token Limit Exceeded, Please try again after a minute`,
          );
          setshowModal(true);
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error('Error:', error);
        setModalTitle('Error');
        setshowModalMessage(
          'Token Limit Exceeded, Please try again after a minute',
        );
        setshowModal(true);
        setIsLoading(false);
      }
      setIsLoading(false);
    }

    if (selectedModel == 'o4_model') {
      try {
        // https://api.openai.com/v1/chat/completions
        const response = await fetch(`${BASE_URI}/api/o4_model/v5?`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(newApiData),
        });

        // Check if response is successful (status code 2xx)
        if (response.ok) {
          setIsLoading(false);

          const data = await response.json();
          if (Number(data?.usage?.total_tokens) >= 25000) {
            const countdown = setInterval(() => {
              let timeLimit = 30;
              setTimer((timeLimit) => {
                if (timeLimit <= 1) {
                  // Time's up
                  clearInterval(countdown);
                  return 0;
                } else {
                  // Decrease timer
                  return timeLimit - 1;
                }
              });
            }, 1000);
            setTimer(30);
          }

          if (data?.choices) {
            setSummary((prev) => [
              ...prev,
              {
                user: prompt,
                content: data?.choices[0]?.message?.content,
                token: data?.usage?.total_tokens,
              },
            ]);

            const RequestBody = {
              _id: convoId,
              chatId: data?.id,
              chatName: chartName,
              sourceAPI: JSON.stringify(SearchObject),
              socialMediaSubType: subtype,
              conversationId: convoId,
              promptName: topPillsName,
              createdTime: data?.created,
              ...buildChartContext({
                handlerUsed: mainSearchTerm,
                socialMediaType,
                searchObject: SearchObject,
              }),
              socialMediaPillsName: PillsName || ChatgptData?.title,
              messages: [
                { role: 'user', content: prompt },
                {
                  role: 'assistant',
                  content: data.choices[0].message.content,
                },
              ],
              usage: data?.usage,
              source: 'o4',
            };

            ConvoHistory(RequestBody)
              .then((res) => {
                // GetAndSetResponse();
              })
              .catch((error) => { });

            setIsLoading(false);
          } else if (data?.message) {
            setModalTitle('Error : 403');
            setshowModalMessage(
              'Token Limit Exceeded, Please try again after a minute',
            );
            setshowModal(true);
          } else {
            setModalTitle('Error : 500');
            setshowModalMessage(
              `Token Limit Exceeded, Please try again after a minute`,
            );
            setshowModal(true);
          }
        } else {
          setIsLoading(false);
          // Handle non-successful response (status code other than 2xx)
          setModalTitle(`Error: ${response.status}`);
          setshowModalMessage(
            `Token Limit Exceeded, Please try again after a minute`,
          );
          setshowModal(true);
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error('Error:', error);
        setModalTitle('Error');
        setshowModalMessage(
          'Token Limit Exceeded, Please try again after a minute',
        );
        setshowModal(true);
        setIsLoading(false);
      }
      setIsLoading(false);
    }

    if (selectedModel == 'llama4_model') {
      try {
        // https://api.openai.com/v1/chat/completions
        const response = await fetch(`${BASE_URI}/api/llama4_model/v5?`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(newApiData),
        });

        // Check if response is successful (status code 2xx)
        if (response.ok) {
          setIsLoading(false);

          const data = await response.json();
          if (Number(data?.usage?.total_tokens) >= 25000) {
            const countdown = setInterval(() => {
              let timeLimit = 30;
              setTimer((timeLimit) => {
                if (timeLimit <= 1) {
                  // Time's up
                  clearInterval(countdown);
                  return 0;
                } else {
                  // Decrease timer
                  return timeLimit - 1;
                }
              });
            }, 1000);
            setTimer(30);
          }

          if (data?.choices) {
            setSummary((prev) => [
              ...prev,
              {
                user: prompt,
                content: data?.choices[0]?.message?.content,
                token: data?.usage?.total_tokens,
              },
            ]);

            const RequestBody = {
              _id: convoId,
              chatId: data?.id,
              chatName: chartName,
              sourceAPI: JSON.stringify(SearchObject),
              socialMediaSubType: subtype,
              conversationId: convoId,
              promptName: topPillsName,
              createdTime: data?.created,
              ...buildChartContext({
                handlerUsed: mainSearchTerm,
                socialMediaType,
                searchObject: SearchObject,
              }),
              socialMediaPillsName: PillsName || ChatgptData?.title,
              messages: [
                { role: 'user', content: prompt },
                {
                  role: 'assistant',
                  content: data.choices[0].message.content,
                },
              ],
              usage: data?.usage,
              source: 'o4',
            };

            ConvoHistory(RequestBody)
              .then((res) => {
                // GetAndSetResponse();
              })
              .catch((error) => { });

            setIsLoading(false);
          } else if (data?.message) {
            setModalTitle('Error : 403');
            setshowModalMessage(
              'Token Limit Exceeded, Please try again after a minute',
            );
            setshowModal(true);
          } else {
            setModalTitle('Error : 500');
            setshowModalMessage(
              `Token Limit Exceeded, Please try again after a minute`,
            );
            setshowModal(true);
          }
        } else {
          setIsLoading(false);
          // Handle non-successful response (status code other than 2xx)
          setModalTitle(`Error: ${response.status}`);
          setshowModalMessage(
            `Token Limit Exceeded, Please try again after a minute`,
          );
          setshowModal(true);
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error('Error:', error);
        setModalTitle('Error');
        setshowModalMessage(
          'Token Limit Exceeded, Please try again after a minute',
        );
        setshowModal(true);
        setIsLoading(false);
      }
      setIsLoading(false);
    }

    if (selectedModel == 'groq3' && (timer == 30 || timer <= 1)) {
      try {
        // https://api.openai.com/v1/chat/completions
        const response = await fetch(`${BASE_URI}/api/grok_model/v5?`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(newApiData),
        });

        // Check if response is successful (status code 2xx)
        if (response.ok) {
          setIsLoading(false);

          const data = await response.json();
          if (Number(data?.usage?.total_tokens) >= 25000) {
            const countdown = setInterval(() => {
              let timeLimit = 30;
              setTimer((timeLimit) => {
                if (timeLimit <= 1) {
                  // Time's up
                  clearInterval(countdown);
                  return 0;
                } else {
                  // Decrease timer
                  return timeLimit - 1;
                }
              });
            }, 1000);
            setTimer(30);
          }

          if (data?.choices) {
            setSummary((prev) => [
              ...prev,
              {
                user: prompt,
                content: data?.choices[0]?.message?.content,
                token: data?.usage?.total_tokens,
              },
            ]);

            const RequestBody = {
              _id: convoId,
              chatId: data?.id,
              chatName: chartName,
              sourceAPI: JSON.stringify(SearchObject),
              socialMediaSubType: subtype,
              conversationId: convoId,
              promptName: topPillsName,
              createdTime: data?.created,
              ...buildChartContext({
                handlerUsed: mainSearchTerm,
                socialMediaType,
                searchObject: SearchObject,
              }),
              socialMediaPillsName: PillsName || ChatgptData?.title,
              messages: [
                { role: 'user', content: prompt },
                {
                  role: 'assistant',
                  content: data.choices[0].message.content,
                },
              ],
              usage: data?.usage,
              source: 'gq3',
            };

            ConvoHistory(RequestBody)
              .then((res) => {
                // GetAndSetResponse();
              })
              .catch((error) => { });

            setIsLoading(false);
          } else if (data?.message) {
            setModalTitle('Error : 403');
            setshowModalMessage(
              'Token Limit Exceeded, Please try again after a minute',
            );
            setshowModal(true);
          } else {
            setModalTitle('Error : 500');
            setshowModalMessage(
              `Token Limit Exceeded, Please try again after a minute`,
            );
            setshowModal(true);
          }
        } else {
          setIsLoading(false);
          // Handle non-successful response (status code other than 2xx)
          setModalTitle(`Error: ${response.status}`);
          setshowModalMessage(
            `Token Limit Exceeded, Please try again after a minute`,
          );
          setshowModal(true);
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error('Error:', error);
        setModalTitle('Error');
        setshowModalMessage(
          'Token Limit Exceeded, Please try again after a minute',
        );
        setshowModal(true);
        setIsLoading(false);
      }
      setIsLoading(false);
    }

    if (selectedModel == 'llamainstant' && (timer == 30 || timer <= 1)) {
      try {
        // https://api.openai.com/v1/chat/completions
        const response = await fetch(`${BASE_URI}/api/llamainstant/v5`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(newApiData),
        });

        // Check if response is successful (status code 2xx)
        if (response.ok) {
          setIsLoading(false);

          const data = await response.json();
          if (Number(data?.usage?.total_tokens) >= 25000) {
            const countdown = setInterval(() => {
              let timeLimit = 30;
              setTimer((timeLimit) => {
                if (timeLimit <= 1) {
                  // Time's up
                  clearInterval(countdown);
                  return 0;
                } else {
                  // Decrease timer
                  return timeLimit - 1;
                }
              });
            }, 1000);
            setTimer(30);
          }

          if (data?.choices) {
            setSummary((prev) => [
              ...prev,
              {
                user: prompt,
                content: data?.choices[0]?.message?.content,
                token: data?.usage?.total_tokens,
              },
            ]);

            const RequestBody = {
              _id: convoId,
              chatId: data?.id,
              chatName: chartName,
              sourceAPI: JSON.stringify(SearchObject),
              socialMediaSubType: subtype,
              conversationId: convoId,
              promptName: topPillsName,
              createdTime: data?.created,
              ...buildChartContext({
                handlerUsed: mainSearchTerm,
                socialMediaType,
                searchObject: SearchObject,
              }),
              socialMediaPillsName: PillsName || ChatgptData?.title,
              messages: [
                { role: 'user', content: prompt },
                {
                  role: 'assistant',
                  content: data.choices[0].message.content,
                },
              ],
              usage: data?.usage,
              source: 'gr',
            };

            ConvoHistory(RequestBody)
              .then((res) => {
                // GetAndSetResponse();
              })
              .catch((error) => { });

            setIsLoading(false);
          } else if (data?.message) {
            setModalTitle('Error : 403');
            setshowModalMessage(
              'Token Limit Exceeded, Please try again after a minute',
            );
            setshowModal(true);
          } else {
            setModalTitle('Error : 500');
            setshowModalMessage(
              `Token Limit Exceeded, Please try again after a minute`,
            );
            setshowModal(true);
          }
        } else {
          setIsLoading(false);
          // Handle non-successful response (status code other than 2xx)
          setModalTitle(`Error: ${response.status}`);
          setshowModalMessage(
            `Token Limit Exceeded, Please try again after a minute`,
          );
          setshowModal(true);
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error('Error:', error);
        setModalTitle('Error');
        setshowModalMessage(
          'Token Limit Exceeded, Please try again after a minute',
        );
        setshowModal(true);
        setIsLoading(false);
      }
      setIsLoading(false);
    }

    if (selectedModel == 'llamaversatile' && (timer == 30 || timer <= 1)) {
      try {
        // https://api.openai.com/v1/chat/completions
        const response = await fetch(`${BASE_URI}/api/llamaversatile/v5`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(newApiData),
        });

        // Check if response is successful (status code 2xx)
        if (response.ok) {
          setIsLoading(false);

          const data = await response.json();
          if (Number(data?.usage?.total_tokens) >= 25000) {
            const countdown = setInterval(() => {
              let timeLimit = 30;
              setTimer((timeLimit) => {
                if (timeLimit <= 1) {
                  // Time's up
                  clearInterval(countdown);
                  return 0;
                } else {
                  // Decrease timer
                  return timeLimit - 1;
                }
              });
            }, 1000);
            setTimer(30);
          }

          if (data?.choices) {
            setSummary((prev) => [
              ...prev,
              {
                user: prompt,
                content: data?.choices[0]?.message?.content,
                token: data?.usage?.total_tokens,
              },
            ]);

            const RequestBody = {
              _id: convoId,
              chatId: data?.id,
              chatName: chartName,
              sourceAPI: JSON.stringify(SearchObject),
              socialMediaSubType: subtype,
              conversationId: convoId,
              promptName: topPillsName,
              createdTime: data?.created,
              ...buildChartContext({
                handlerUsed: mainSearchTerm,
                socialMediaType,
                searchObject: SearchObject,
              }),
              socialMediaPillsName: PillsName || ChatgptData?.title,
              messages: [
                { role: 'user', content: prompt },
                {
                  role: 'assistant',
                  content: data.choices[0].message.content,
                },
              ],
              usage: data?.usage,
              source: 'gr',
            };

            ConvoHistory(RequestBody)
              .then((res) => {
                // GetAndSetResponse();
              })
              .catch((error) => { });

            setIsLoading(false);
          } else if (data?.message) {
            setModalTitle('Error : 403');
            setshowModalMessage(
              'Token Limit Exceeded, Please try again after a minute',
            );
            setshowModal(true);
          } else {
            setModalTitle('Error : 500');
            setshowModalMessage(
              `Token Limit Exceeded, Please try again after a minute`,
            );
            setshowModal(true);
          }
        } else {
          setIsLoading(false);
          // Handle non-successful response (status code other than 2xx)
          setModalTitle(`Error: ${response.status}`);
          setshowModalMessage(
            `Token Limit Exceeded, Please try again after a minute`,
          );
          setshowModal(true);
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error('Error:', error);
        setModalTitle('Error');
        setshowModalMessage(
          'Token Limit Exceeded, Please try again after a minute',
        );
        setshowModal(true);
        setIsLoading(false);
      }
      setIsLoading(false);
    }
    if (selectedModel == 'llamadeepseek' && (timer == 30 || timer <= 1)) {
      try {
        // https://api.openai.com/v1/chat/completions
        const response = await fetch(`${BASE_URI}/api/llamadeepseek/v5`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(newApiData),
        });

        // Check if response is successful (status code 2xx)
        if (response.ok) {
          setIsLoading(false);

          const data = await response.json();
          if (Number(data?.usage?.total_tokens) >= 25000) {
            const countdown = setInterval(() => {
              let timeLimit = 30;
              setTimer((timeLimit) => {
                if (timeLimit <= 1) {
                  // Time's up
                  clearInterval(countdown);
                  return 0;
                } else {
                  // Decrease timer
                  return timeLimit - 1;
                }
              });
            }, 1000);
            setTimer(30);
          }

          if (data?.choices) {
            setSummary((prev) => [
              ...prev,
              {
                user: prompt,
                content: data?.choices[0]?.message?.content,
                token: data?.usage?.total_tokens,
              },
            ]);

            const RequestBody = {
              _id: convoId,
              chatId: data?.id,
              chatName: chartName,
              sourceAPI: JSON.stringify(SearchObject),
              socialMediaSubType: subtype,
              conversationId: convoId,
              promptName: topPillsName,
              createdTime: data?.created,
              ...buildChartContext({
                handlerUsed: mainSearchTerm,
                socialMediaType,
                searchObject: SearchObject,
              }),
              socialMediaPillsName: PillsName || ChatgptData?.title,
              messages: [
                { role: 'user', content: prompt },
                {
                  role: 'assistant',
                  content: data.choices[0].message.content,
                },
              ],
              usage: data?.usage,
              source: 'gr',
            };

            ConvoHistory(RequestBody)
              .then((res) => {
                // GetAndSetResponse();
              })
              .catch((error) => { });

            setIsLoading(false);
          } else if (data?.message) {
            setModalTitle('Error : 403');
            setshowModalMessage(
              'Token Limit Exceeded, Please try again after a minute',
            );
            setshowModal(true);
          } else {
            setModalTitle('Error : 500');
            setshowModalMessage(
              `Token Limit Exceeded, Please try again after a minute`,
            );
            setshowModal(true);
          }
        } else {
          setIsLoading(false);
          // Handle non-successful response (status code other than 2xx)
          setModalTitle(`Error: ${response.status}`);
          setshowModalMessage(
            `Token Limit Exceeded, Please try again after a minute`,
          );
          setshowModal(true);
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error('Error:', error);
        setModalTitle('Error');
        setshowModalMessage(
          'Token Limit Exceeded, Please try again after a minute',
        );
        setshowModal(true);
        setIsLoading(false);
      }
      setIsLoading(false);
    }
    // if (selectedModel == 'llamaguard' && (timer == 30 || timer <= 1)) {
    //   try {
    //   //   // https://api.openai.com/v1/chat/completions
    //   //   const response = await fetch(`${BASE_URI}/api/llamaguard/v5`, {
    //   //     method: 'POST',
    //   //     headers: {
    //   //       'Content-Type': 'application/json',
    //   //       Authorization: `Bearer ${getToken()}`,
    //   //     },
    //   //     body: JSON.stringify(newApiData),
    //   //   });
    //   //   // Check if response is successful (status code 2xx)
    //   //   if (response.ok) {
    //   //     setIsLoading(false);
    //   //     const data = await response.json();
    //   //     if (Number(data?.usage?.total_tokens) >= 25000) {
    //   //       const countdown = setInterval(() => {
    //   //         let timeLimit = 30;
    //   //         setTimer((timeLimit) => {
    //   //           if (timeLimit <= 1) {
    //   //             // Time's up
    //   //             clearInterval(countdown);
    //   //             return 0;
    //   //           } else {
    //   //             // Decrease timer
    //   //             return timeLimit - 1;
    //   //           }
    //   //         });
    //   //       }, 1000);
    //   //       setTimer(30);
    //   //     }
    //   //     if (data?.choices) {
    //   //       setSummary((prev) => [
    //   //         ...prev,
    //   //         {
    //   //           user: prompt,
    //   //           content: data?.choices[0]?.message?.content,
    //   //           token: data?.usage?.total_tokens,
    //   //         },
    //   //       ]);
    //   //       const RequestBody = {
    //   //         _id: convoId,
    //   //         chatId: data?.id,
    //   //         chatName: chartName,
    //   //         sourceAPI: JSON.stringify(SearchObject),
    //   //         socialMediaSubType: subtype,
    //   //         conversationId: convoId,
    //   //         promptName: topPillsName,
    //   //         createdTime: data?.created,
    //   //         socialMediaType: Array.isArray(socialMediaType)
    //   //           ? socialMediaType // Convert array to comma-separated string
    //   //           : [socialMediaType],
    //   //         handlerUsed: mainSearchTerm.toLowerCase(),
    //   //         interval: SearchObject?.interval,
    //   //         socialMediaPillsName: PillsName || ChatgptData?.title,
    //   //         messages: [
    //   //           { role: 'user', content: prompt },
    //   //           {
    //   //             role: 'assistant',
    //   //             content: data.choices[0].message.content,
    //   //           },
    //   //         ],
    //   //         usage: data?.usage,
    //   //         source: 'gr',
    //   //       };
    //   //       ConvoHistory(RequestBody)
    //   //         .then((res) => {
    //   //           // GetAndSetResponse();
    //   //         })
    //   //         .catch((error) => {});
    //   //       setIsLoading(false);
    //   //     } else if (data?.message) {
    //   //       setModalTitle('Error : 403');
    //   //       setshowModalMessage(
    //   //         'Token Limit Exceeded, Please try again after a minute',
    //   //       );
    //   //       setshowModal(true);
    //   //     } else {
    //   //       setModalTitle('Error : 500');
    //   //       setshowModalMessage(
    //   //         `Token Limit Exceeded, Please try again after a minute`,
    //   //       );
    //   //       setshowModal(true);
    //   //     }
    //   //   } else {
    //   //     setIsLoading(false);
    //   //     // Handle non-successful response (status code other than 2xx)
    //   //     setModalTitle(`Error: ${response.status}`);
    //   //     setshowModalMessage(
    //   //       `Token Limit Exceeded, Please try again after a minute`,
    //   //     );
    //   //     setshowModal(true);
    //   //   }
    //   // } catch (error) {
    //   //   // Handle network errors or other exceptions
    //   //   console.error('Error:', error);
    //   //   setModalTitle('Error');
    //   //   setshowModalMessage(
    //   //     'Token Limit Exceeded, Please try again after a minute',
    //   //   );
    //   //   setshowModal(true);
    //   //   setIsLoading(false);
    //   // }
    //   // setIsLoading(false);
    // }

    if (selectedModel == 'mx_groq_llama') {
      try {
        // https://api.openai.com/v1/chat/completions
        const response = await fetch(`${BASE_URI}/api/groq_llama3_model/v5`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(newApiData),
        });

        // Check if response is successful (status code 2xx)
        if (response.ok) {
          setIsLoading(false);
          const data = await response.json();
          if (data?.choices) {
            setSummary((prev) => [
              ...prev,
              {
                user: prompt,
                content: data?.choices[0]?.message?.content,
                token: data?.usage?.total_tokens,
              },
            ]);

            const RequestBody = {
              _id: convoId,
              chatId: data?.id,
              chatName: chartName,
              sourceAPI: JSON.stringify(SearchObject),
              socialMediaSubType: subtype,
              conversationId: convoId,
              promptName: topPillsName,
              createdTime: data?.created,
              ...buildChartContext({
                handlerUsed: mainSearchTerm,
                socialMediaType,
                searchObject: SearchObject,
              }),
              socialMediaPillsName: PillsName || ChatgptData?.title,
              messages: [
                { role: 'user', content: prompt },
                {
                  role: 'assistant',
                  content: data.choices[0].message.content,
                },
              ],
              usage: data?.usage,
              source: 'grl',
            };

            ConvoHistory(RequestBody)
              .then((res) => {
                // GetAndSetResponse();
              })
              .catch((error) => { });

            setIsLoading(false);
          } else if (data?.message) {
            setModalTitle('Error : 403');
            setshowModalMessage(data?.message);
            setshowModal(true);
          } else {
            setModalTitle('Error : 500');
            setshowModalMessage(`Please refresh and Login if needed`);
            setshowModal(true);
          }
        } else {
          setIsLoading(false);
          // Handle non-successful response (status code other than 2xx)
          setModalTitle(`Error: ${response.status}`);
          setshowModalMessage(
            `Request failed with status ${response.message || response.status}
            `,
          );
          setshowModal(true);
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error('Error:', error);
        setModalTitle('Error');
        setshowModalMessage('An error occurred while processing your request.');
        setshowModal(true);
        setIsLoading(false);
      }
      setIsLoading(false);
    }

    // if (selectedModel == 'mixt-56b') {
    //   // try {
    //   //   // https://api.openai.com/v1/chat/completions
    //   //   await fetch('https://ainfinity.co/api/mixt_56b/', {
    //   //     method: 'POST',
    //   //     headers: {
    //   //       'Content-Type': 'application/json',
    //   //     },
    //   //     body: `${JSON.stringify(newApiData)}`,
    //   //   })
    //   //     .then((data) => {
    //   //       return data.json();
    //   //     })
    //   //     .then((data) => {
    //   //       if (data?.choices) {
    //   //         setSummary((prev) => [
    //   //           ...prev,
    //   //           {
    //   //             user: prompt,
    //   //             content: data?.choices[0]?.message?.content,
    //   //             token: data?.usage?.total_tokens,
    //   //           },
    //   //         ]);
    //   //       } else {
    //   //         setModalTitle('Error : 403');
    //   //         setshowModalMessage(`Please refresh and Login if needed`);
    //   //         setshowModal(true);
    //   //         // setTimeout(() => {
    //   //         //   setshowModal(false);
    //   //         // }, 10000);
    //   //       }
    //   //       const RequestBody = {
    //   //         _id: convoId,
    //   //         chatId: data?.id,
    //   //         chatName: chartName,
    //   //         sourceAPI: JSON.stringify(SearchObject),
    //   //         socialMediaSubType: subtype,
    //   //         conversationId: convoId,
    //   //         promptName: topPillsName,
    //   //         createdTime: data?.created,
    //   //         socialMediaType: Array.isArray(socialMediaType)
    //   //           ? socialMediaType // Convert array to comma-separated string
    //   //           : [socialMediaType],
    //   //         handlerUsed: mainSearchTerm.toLowerCase(),
    //   //         interval: SearchObject?.interval,
    //   //         socialMediaPillsName: PillsName || ChatgptData?.title,
    //   //         messages: [
    //   //           { role: 'user', content: prompt },
    //   //           {
    //   //             role: 'assistant',
    //   //             content: data.choices[0].message.content,
    //   //           },
    //   //         ],
    //   //         usage: data?.usage,
    //   //         source: 'm56',
    //   //       };
    //   //       ConvoHistory(RequestBody)
    //   //         .then((res) => {})
    //   //         .catch((error) => {});
    //   //       setIsLoading(false);
    //   //     });
    //   // } catch (error) {
    //   //   setIsLoading(false);
    //   // }
    // }

    // if (selectedModel == 'internlm') {
    //   // try {
    //   //   // https://api.openai.com/v1/chat/completions
    //   //   await fetch('https://ainfinity.co/api/internlm/', {
    //   //     method: 'POST',
    //   //     headers: {
    //   //       'Content-Type': 'application/json',
    //   //     },
    //   //     body: `${JSON.stringify(newApiData)}`,
    //   //   })
    //   //     .then((data) => {
    //   //       return data.json();
    //   //     })
    //   //     .then((data) => {
    //   //       if (data?.choices) {
    //   //         setSummary((prev) => [
    //   //           ...prev,
    //   //           {
    //   //             user: prompt,
    //   //             content: data?.choices[0]?.message?.content,
    //   //             token: data?.usage?.total_tokens,
    //   //           },
    //   //         ]);
    //   //       } else {
    //   //         setModalTitle('Error : 403');
    //   //         setshowModalMessage(`Please refresh and Login if needed`);
    //   //         setshowModal(true);
    //   //         // setTimeout(() => {
    //   //         //   setshowModal(false);
    //   //         // }, 10000);
    //   //       }
    //   //       const RequestBody = {
    //   //         _id: convoId,
    //   //         chatId: data?.id,
    //   //         chatName: chartName,
    //   //         sourceAPI: JSON.stringify(SearchObject),
    //   //         socialMediaSubType: subtype,
    //   //         conversationId: convoId,
    //   //         promptName: topPillsName,
    //   //         createdTime: data?.created,
    //   //         socialMediaType: Array.isArray(socialMediaType)
    //   //           ? socialMediaType // Convert array to comma-separated string
    //   //           : [socialMediaType],
    //   //         handlerUsed: mainSearchTerm.toLowerCase(),
    //   //         interval: SearchObject?.interval,
    //   //         socialMediaPillsName: PillsName || ChatgptData?.title,
    //   //         messages: [
    //   //           { role: 'user', content: prompt },
    //   //           {
    //   //             role: 'assistant',
    //   //             content: data.choices[0].message.content,
    //   //           },
    //   //         ],
    //   //         usage: data?.usage,
    //   //         source: 'in',
    //   //       };
    //   //       ConvoHistory(RequestBody)
    //   //         .then((res) => {})
    //   //         .catch((error) => {});
    //   //       setIsLoading(false);
    //   //     });
    //   // } catch (error) {
    //   //   setIsLoading(false);
    //   // }
    // }

    // if (selectedModel == 'qwen') {
    //   // try {
    //   //   // https://api.openai.com/v1/chat/completions
    //   //   await fetch('https://ainfinity.co/api/qwen/', {
    //   //     method: 'POST',
    //   //     headers: {
    //   //       'Content-Type': 'application/json',
    //   //     },
    //   //     body: `${JSON.stringify(newApiData)}`,
    //   //   })
    //   //     .then((data) => {
    //   //       return data.json();
    //   //     })
    //   //     .then((data) => {
    //   //       if (data?.choices) {
    //   //         setSummary((prev) => [
    //   //           ...prev,
    //   //           {
    //   //             user: prompt,
    //   //             content: data?.choices[0]?.message?.content,
    //   //             token: data?.usage?.total_tokens,
    //   //           },
    //   //         ]);
    //   //       } else {
    //   //         setModalTitle('Error : 403');
    //   //         setshowModalMessage(`Please refresh and Login if needed`);
    //   //         setshowModal(true);
    //   //         // setTimeout(() => {
    //   //         //   setshowModal(false);
    //   //         // }, 10000);
    //   //       }
    //   //       const RequestBody = {
    //   //         _id: convoId,
    //   //         chatId: data?.id,
    //   //         chatName: chartName,
    //   //         sourceAPI: JSON.stringify(SearchObject),
    //   //         socialMediaSubType: subtype,
    //   //         conversationId: convoId,
    //   //         promptName: topPillsName,
    //   //         createdTime: data?.created,
    //   //         socialMediaType: Array.isArray(socialMediaType)
    //   //           ? socialMediaType // Convert array to comma-separated string
    //   //           : [socialMediaType],
    //   //         handlerUsed: mainSearchTerm.toLowerCase(),
    //   //         interval: SearchObject?.interval,
    //   //         socialMediaPillsName: PillsName || ChatgptData?.title,
    //   //         messages: [
    //   //           { role: 'user', content: prompt },
    //   //           {
    //   //             role: 'assistant',
    //   //             content: data.choices[0].message.content,
    //   //           },
    //   //         ],
    //   //         usage: data?.usage,
    //   //         source: 'qw',
    //   //       };
    //   //       ConvoHistory(RequestBody)
    //   //         .then((res) => {})
    //   //         .catch((error) => {});
    //   //       setIsLoading(false);
    //   //     });
    //   // } catch (error) {
    //   //   setIsLoading(false);
    //   // }
    // }
    // if (selectedModel == 'mixt-large') {
    //   // const requestdata = {
    //   //   messages: [
    //   //     { role: 'system', content: `You are a helpful assistant.` },
    //   //     { role: 'user', content: prompt + JSON.stringify(theJsonData) },
    //   //   ],
    //   // };
    //   // try {
    //   //   // https://api.openai.com/v1/chat/completions
    //   //   await fetch(`${BASE_URI}/api/call_mistral_model/v5`, {
    //   //     method: 'POST',
    //   //     headers: {
    //   //       'Content-Type': 'application/json',
    //   //       Authorization: `Bearer ${getToken()}`,
    //   //     },
    //   //     body: JSON.stringify(requestdata),
    //   //     // body: `${JSON.stringify(newApiData)}`,
    //   //     //        const newApiData = {
    //   //     //   prompt: `${
    //   //     //     lang
    //   //     //       ? `write the output in ${lang} script language only (do not use English unless specified) for this prompt: ${prompt}`
    //   //     //       : prompt
    //   //     //   }`,
    //   //     //   content: `${JSON.stringify(theJsonData)}`,
    //   //     //   // content: theJsonData,
    //   //     // };
    //   //   })
    //   //     .then((data) => {
    //   //       return data.json();
    //   //     })
    //   //     .then((data) => {
    //   //       if (data?.choices) {
    //   //         setSummary((prev) => [
    //   //           ...prev,
    //   //           {
    //   //             user: prompt,
    //   //             content: data?.choices[0]?.message?.content,
    //   //             token: data?.usage?.total_tokens,
    //   //           },
    //   //         ]);
    //   //       } else {
    //   //         setModalTitle('Error : 403');
    //   //         setshowModalMessage(`Please refresh and Login if needed`);
    //   //         setshowModal(true);
    //   //         // setTimeout(() => {
    //   //         //   setshowModal(false);
    //   //         // }, 10000);
    //   //       }
    //   //       const RequestBody = {
    //   //         _id: convoId,
    //   //         chatId: data?.id,
    //   //         chatName: chartName,
    //   //         sourceAPI: JSON.stringify(SearchObject),
    //   //         socialMediaSubType: subtype,
    //   //         conversationId: convoId,
    //   //         promptName: topPillsName,
    //   //         createdTime: data?.created,
    //   //         socialMediaType: Array.isArray(socialMediaType)
    //   //           ? socialMediaType // Convert array to comma-separated string
    //   //           : [socialMediaType],
    //   //         handlerUsed: mainSearchTerm.toLowerCase(),
    //   //         interval: SearchObject?.interval,
    //   //         socialMediaPillsName: PillsName || ChatgptData?.title,
    //   //         messages: [
    //   //           { role: 'user', content: prompt },
    //   //           {
    //   //             role: 'assistant',
    //   //             content: data.choices[0].message.content,
    //   //           },
    //   //         ],
    //   //         usage: data?.usage,
    //   //         source: 'ml',
    //   //       };
    //   //       ConvoHistory(RequestBody)
    //   //         .then((res) => {})
    //   //         .catch((error) => {});
    //   //       setIsLoading(false);
    //   //     });
    //   // } catch (error) {
    //   //   setIsLoading(false);
    //   // }
    // }

    // if (selectedModel == 'Claude') {
    //   // const requestdata = {
    //   //   input_text: prompt + JSON.stringify(theJsonData),
    //   //   input_key: apikeyValue,
    //   //   system_prompt_instructions: selectInstruction,
    //   // };
    //   // try {
    //   //   await fetch(`${BASE_URI}/api/claude/v5`, {
    //   //     method: 'POST',
    //   //     headers: {
    //   //       'Content-Type': 'application/json',
    //   //       Authorization: `Bearer ${getToken()}`,
    //   //     },
    //   //     body: JSON.stringify(requestdata),
    //   //     // body: `${JSON.stringify(newApiData)}`,
    //   //     //        const newApiData = {
    //   //     //   prompt: `${
    //   //     //     lang
    //   //     //       ? `write the output in ${lang} script language only (do not use English unless specified) for this prompt: ${prompt}`
    //   //     //       : prompt
    //   //     //   }`,
    //   //     //   content: `${JSON.stringify(theJsonData)}`,
    //   //     //   // content: theJsonData,
    //   //     // };
    //   //   })
    //   //     .then((data) => {
    //   //       return data.json();
    //   //     })
    //   //     .then((data) => {
    //   //       if (data?.choices) {
    //   //         setSummary((prev) => [
    //   //           ...prev,
    //   //           {
    //   //             user: prompt,
    //   //             content: data?.choices[0]?.message?.content,
    //   //             token: data?.usage?.total_tokens,
    //   //           },
    //   //         ]);
    //   //       } else {
    //   //         setModalTitle('Error : 403');
    //   //         setshowModalMessage(`Please refresh and Login if needed`);
    //   //         setshowModal(true);
    //   //         // setTimeout(() => {
    //   //         //   setshowModal(false);
    //   //         // }, 10000);
    //   //       }
    //   //       const RequestBody = {
    //   //         _id: convoId,
    //   //         chatId: data?.id,
    //   //         chatName: chartName,
    //   //         sourceAPI: JSON.stringify(SearchObject),
    //   //         socialMediaSubType: subtype,
    //   //         conversationId: convoId,
    //   //         promptName: topPillsName,
    //   //         createdTime: data?.created,
    //   //         socialMediaType: Array.isArray(socialMediaType)
    //   //           ? socialMediaType // Convert array to comma-separated string
    //   //           : [socialMediaType],
    //   //         handlerUsed: mainSearchTerm.toLowerCase(),
    //   //         interval: SearchObject?.interval,
    //   //         socialMediaPillsName: PillsName || ChatgptData?.title,
    //   //         messages: [
    //   //           { role: 'user', content: prompt },
    //   //           {
    //   //             role: 'assistant',
    //   //             content: data.choices[0].message.content,
    //   //           },
    //   //         ],
    //   //         usage: data?.usage,
    //   //         source: 'cl',
    //   //       };
    //   //       ConvoHistory(RequestBody)
    //   //         .then((res) => {})
    //   //         .catch((error) => {});
    //   //       setIsLoading(false);
    //   //     });
    //   // } catch (error) {
    //   //   setIsLoading(false);
    //   // }
    // }
    // if (selectedModel == 'llama3_gradient') {
    //   // try {
    //   //   // https://api.openai.com/v1/chat/completions
    //   //   await fetch('https://haixgenai.com/api/llama3_gradient/v5?', {
    //   //     method: 'POST',
    //   //     headers: {
    //   //       'Content-Type': 'application/json',
    //   //       // Authorization: `Bearer ${getToken()}`,
    //   //     },
    //   //     body: `${JSON.stringify(newApiDataNewModel)}`,
    //   //   })
    //   //     .then((data) => {
    //   //       return data.json();
    //   //     })
    //   //     .then((data) => {
    //   //       if (data?.choices) {
    //   //         setSummary((prev) => [
    //   //           ...prev,
    //   //           {
    //   //             user: prompt,
    //   //             content: data?.choices[0]?.message?.content,
    //   //             token: data?.usage?.total_tokens,
    //   //           },
    //   //         ]);
    //   //       } else {
    //   //         setModalTitle('Error : 403');
    //   //         setshowModalMessage(`Please refresh and Login if needed`);
    //   //         setshowModal(true);
    //   //         // setTimeout(() => {
    //   //         //   setshowModal(false);
    //   //         // }, 10000);
    //   //       }
    //   //       const RequestBody = {
    //   //         _id: convoId,
    //   //         chatId: data?.id,
    //   //         chatName: chartName,
    //   //         sourceAPI: JSON.stringify(SearchObject),
    //   //         socialMediaSubType: subtype,
    //   //         conversationId: convoId,
    //   //         promptName: topPillsName,
    //   //         createdTime: data?.created,
    //   //         socialMediaType: Array.isArray(socialMediaType)
    //   //           ? socialMediaType // Convert array to comma-separated string
    //   //           : [socialMediaType],
    //   //         handlerUsed: mainSearchTerm.toLowerCase(),
    //   //         interval: SearchObject?.interval,
    //   //         socialMediaPillsName: PillsName || ChatgptData?.title,
    //   //         messages: [
    //   //           { role: 'user', content: prompt },
    //   //           { role: 'assistant', content: data.choices[0].content },
    //   //         ],
    //   //         usage: data?.usage,
    //   //         source: selectedModel == 'lm',
    //   //       };
    //   //       ConvoHistory(RequestBody)
    //   //         .then((res) => {})
    //   //         .catch((error) => {});
    //   //       setIsLoading(false);
    //   //     });
    //   // } catch (error) {
    //   //   setIsLoading(false);
    //   // }
    // }

    // if (selectedModel == 'llama3_gradient_new') {
    //   // try {
    //   //   // https://api.openai.com/v1/chat/completions
    //   //   await fetch('https://ainfinity.co/api/llama3_gradient/v5?', {
    //   //     method: 'POST',
    //   //     headers: {
    //   //       'Content-Type': 'application/json',
    //   //     },
    //   //     body: `${JSON.stringify(newApiDataNewModel)}`,
    //   //   })
    //   //     .then((data) => {
    //   //       return data.json();
    //   //     })
    //   //     .then((data) => {
    //   //       if (data?.choices) {
    //   //         setSummary((prev) => [
    //   //           ...prev,
    //   //           {
    //   //             user: prompt,
    //   //             content: data?.choices[0]?.message?.content,
    //   //             token: data?.usage?.total_tokens,
    //   //           },
    //   //         ]);
    //   //       } else {
    //   //         setModalTitle('Error : 403');
    //   //         setshowModalMessage(`Please refresh and Login if needed`);
    //   //         setshowModal(true);
    //   //         // setTimeout(() => {
    //   //         //   setshowModal(false);
    //   //         // }, 10000);
    //   //       }
    //   //       const RequestBody = {
    //   //         _id: convoId,
    //   //         chatId: data?.id,
    //   //         chatName: chartName,
    //   //         sourceAPI: JSON.stringify(SearchObject),
    //   //         socialMediaSubType: subtype,
    //   //         conversationId: convoId,
    //   //         promptName: topPillsName,
    //   //         createdTime: data?.created,
    //   //         socialMediaType: Array.isArray(socialMediaType)
    //   //           ? socialMediaType // Convert array to comma-separated string
    //   //           : [socialMediaType],
    //   //         handlerUsed: mainSearchTerm.toLowerCase(),
    //   //         interval: SearchObject?.interval,
    //   //         socialMediaPillsName: PillsName || ChatgptData?.title,
    //   //         messages: [
    //   //           { role: 'user', content: prompt },
    //   //           {
    //   //             role: 'assistant',
    //   //             content: data.choices[0].content,
    //   //           },
    //   //         ],
    //   //         usage: data?.usage,
    //   //         source: 'lmg',
    //   //       };
    //   //       ConvoHistory(RequestBody)
    //   //         .then((res) => {})
    //   //         .catch((error) => {});
    //   //       setIsLoading(false);
    //   //     });
    //   // } catch (error) {
    //   //   setIsLoading(false);
    //   // }
    // }

    if (
      selectedModel == 'llamaguard' ||
      selectedModel == 'mixt-56b' ||
      selectedModel == 'internlm' ||
      selectedModel == 'qwen' ||
      selectedModel == 'mixt-large' ||
      selectedModel == 'Claude' ||
      selectedModel == 'llama3_gradient' ||
      selectedModel == 'llama3_gradient_new'
    ) {
      alert(
        'For subscription access to use the model, please contact our Customer Success team at team@omniosocial.com.',
      );
    }

    if (selectedModel == 'openai-azure') {
      try {
        const data = await streamAzureResponse({
          apiKey: process.env.REACT_APP_QUICK_SUMMARY_API_KEY,
          instructions: apiRequestBody.messages[0].content,
          input: apiRequestBody.messages[1].content,
        });
        setIsLoading(false);
        setSummary((prev) => [
          ...prev,
          {
            user: prompt,
            content: data.text,
            token: data.usage?.total_tokens,
          },
        ]);
        const RequestBody = {
          _id: convoId,
          chatId: data.id,
          chatName: chartName,
          sourceAPI: JSON.stringify(SearchObject),
          socialMediaSubType: subtype,
          conversationId: convoId,
          promptName: topPillsName,
          createdTime: data.created,
          ...buildChartContext({
            handlerUsed: mainSearchTerm,
            socialMediaType,
            searchObject: SearchObject,
          }),
          socialMediaPillsName: PillsName || ChatgptData?.title,
          messages: [
            { role: 'user', content: prompt },
            { role: 'assistant', content: data.text },
          ],
          usage: data.usage,
          source: selectedModel == 'Default' ? 'ag' : 'ms',
        };

        ConvoHistory(RequestBody)
          .then((res) => {})
          .catch((error) => {});
      } catch (error) {
        const { title, message } = describeAzureError(error);
        setModalTitle(title);
        setshowModalMessage(message);
        setshowModal(true);
      }
      setIsLoading(false);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    if (prompt?.length > 0) {
      setIsPromptData(true);
    } else {
      setIsPromptData(false);
    }
  }, [prompt]);

  const handleFeedBtn = async () => {
    if (isLoading || isAiLoading || !String(prompt || '').trim()) return;
    setCountAPIcall(countAPIcall + 1);
    setIsAiLoading(true);

    if (ChatgptData != null) {
      const count = await handleGetApiCountByUser();
      const tokenCount = await getTokonCount();

      if (count >= totalAvailableApiCount) {
        setModalTitle('Subscription');
        setshowModalMessage(
          'You have exceeded the Co-Pilot Assistant Limit. Subscribe link for more details.',
        );
        setShowModalSubscribe(true);
        setIsLoading(false);
        setIsAiLoading(false);
        setIsLoading(false);
      } else {
        setIsLoading(true);
        await feedToChatGPT(ChatgptData?.excelData);
        setIsAiLoading(false);
      }
    }
  };

  // "Regenerate" on a cached answer - tells /api/router_model_cached/v5 to
  // skip its own cache (exact-repeat or same-chart match) and make a new,
  // paid call for this exact prompt instead.
  const handleRegenerate = async () => {
    if (!ChatgptData?.excelData?.length || isLoading || isAiLoading) return;
    setIsAiLoading(true);
    setIsLoading(true);
    await feedToChatGPT(ChatgptData.excelData, { forceRefresh: true });
    setIsAiLoading(false);
  };

  const [postPromptData, setPostPromptData] = useState({
    promptname: topPillsName,
    promptvalue: prompt,
    chartName: 'Test...',
  });

  useEffect(() => {
    const delay = 1000;
    const timerId = setTimeout(() => {
      // handleGetRequest();
    }, delay);
    return () => clearTimeout(timerId);
  }, []);

  // getToken() reads a plain in-memory value set by AppBase's async
  // initActions() (after getAccessTokenSilently() resolves) — there's no
  // guarantee that finishes within any fixed delay, especially right after
  // a tab wake-from-sleep where Auth0 re-auth is slow. A blind setTimeout
  // here raced that and fired with `Authorization: Bearer undefined`,
  // getting 401'd. profileContext.profileInfo is populated by the same
  // initActions() call, via a request that itself needs a valid token
  // (fetchUserProfile -> axiosInstance -> getToken()), so waiting for it
  // is a reliable proxy for "the token is actually ready" instead of a guess.
  // Also gated on sidebarStatus — this component stays mounted for the whole
  // session (toggled via a CSS 'hidden' class, not conditional rendering),
  // so firing on mount meant every page load paid for this fetch even when
  // the user never opened the AI panel. Wait for it to actually open.
  const hasFetchedTokenCountRef = useRef(false);
  useEffect(() => {
    if (hasFetchedTokenCountRef.current) return;
    if (!profileContext?.profileInfo || !sidebarStatus) return;
    hasFetchedTokenCountRef.current = true;
    getTokonCount();
  }, [profileContext?.profileInfo, sidebarStatus]);

  useEffect(() => {
    const delay = 1000;
    const timerId = setTimeout(() => {
      setHIstoryPillsValue([]);
      setPromptPillsArray([]);
      setInitialData([]);
      if (sidebarStatus) {
        GetAndSetResponse();
        handleGetRequest();
      }
    }, delay);
    return () => clearTimeout(timerId);
  }, [ChatgptData, selectedModel, sidebarStatus]);

  useEffect(() => {
    if (ChatgptData?.title) {
      handleGetRequestInititalDataDefalt();
      // handleGetRequestInititalData();
    }
  }, [ChatgptData, selectedModel]);

  const handlePostRequest = async () => {
    const newPromptData = {
      promptName: newPromptName,
      promptValue: newPromptValue,
      chartName: ChatgptData ? ChatgptData.title : '',
      socialMediaName: computeMultiPlatformNames(SearchObject, socialMediaType),
      source: sourceData[selectedModel],
    };
    try {
      // Replace 'http://172.187.179.39:3002/api/promptnameupdate/v5' with your actual API endpoint
      const response = await fetch(
        `${BASE_URI}/api/promptnameupdate/v5?source=${sourceData[selectedModel]} `,
        {
          method: 'POST',
          headers: {
            // Authorization:
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
            // "api-key": process.env.REACT_APP_CHATGPT_API_KEY,
          },
          body: JSON.stringify(newPromptData),
        },
      );

      if (response.ok) {
        setModalTitle('Add Prompts');
        setshowModalMessage(`Prompt Added Successfully`);
        setshowModal(true);
        setTimeout(() => setshowModal(false), 2000);
      }
      if (!response.ok) {
        // Handle non-2xx responses
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      // Handle the response data as needed
      setIsClickedAddNew(false);
      setNewPromptName('');
      setNewPromptValue('');
      handleGetRequest();
    } catch (error) {
      // Handle errors
      console.error('Error in Post Request:', error.message);
    }
  };

  const handleGetRequest = async () => {
    try {
      // Deliberately NOT filtering by socialMediaName: this collection
      // (gptUserCustomPromptData, written by "+ New prompt") stores it as a
      // real array (e.g. ["instagram","tiktok","youtube"]), but a query
      // value can only be a single string/scalar — Mongo matches a scalar
      // against INDIVIDUAL array elements, never against a comma-joined
      // string standing in for the whole array, so a multi-platform save
      // could never be found this way (confirmed empty response, live).
      // `source` must be sent — confirmed live that omitting it returns
      // empty (this endpoint silently defaults to some other value, same
      // pattern as default_prompt_name_history's unconditional "gr"
      // default), even though it happened to look fine right after the
      // socialMediaName fix (that test predated this regression). A
      // prompt only shows up when queried under the same source it was
      // saved with — fixing that properly needs the backend to make this
      // field conditional/optional the same way chartName already is.
      const url = `${BASE_URI}/api/promptnamehistory/v5?&source=${sourceData[selectedModel]
        }&chartName=${encodeURIComponent(ChatgptData?.title)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();

      setApiPromptData(responseData);
      setPromptInitialData([...promptInitialData, ...responseData]);
      // Handle the response data as needed
    } catch (error) {
      // Handle errors
      console.error('Error in Get Request:', error.message);
    }
  };

  // useEffect(() => {
  //   handleGetRequest();
  // }, [selectedModel]);
  const handleGetApiCountByUser = async () => {
    try {
      // Replace 'https://alivesearch.live/api/promptnamehistory/v5' with your actual API endpoint
      // const queryString = new URLSearchParams(newPromptData).toString();
      const url = `${BASE_URI}/api/stats/userHistory?task=count`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          // Authorization:
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
          // Authorization: `Bearer ${getToken()}`,
          // "api-key": process.env.REACT_APP_CHATGPT_API_KEY,
        },
      });

      if (!response.ok) {
        // Handle non-2xx responses
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      setApiCount(responseData?.prompt_count);

      return responseData?.prompt_count;
    } catch (error) {
      // Handle errors
      console.error('Error in Get Request:', error.message);
      return 0;
    }
  };

  const getTokonCount = async () => {
    try {
      // Replace 'https://alivesearch.live/api/promptnamehistory/v5' with your actual API endpoint
      // const queryString = new URLSearchParams(newPromptData).toString();
      const url = `${BASE_URI}/api/stats/userHistory?task=token`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          // Authorization:
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
          // Authorization: `Bearer ${getToken()}`,
          // "api-key": process.env.REACT_APP_CHATGPT_API_KEY,
        },
      });

      if (!response.ok) {
        // Handle non-2xx responses
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      setTokenUsed(responseData?.total_tokens_by_user);

      return responseData?.total_tokens_by_user;
    } catch (error) {
      // Handle errors
      console.error('Error in Get Request:', error.message);
      return 0;
    }
  };

  const handleGetRequestInititalDataDefalt = async () => {
    try {
      const shouldUseLinkedIn =
        ChatgptData?.title === 'Active Audience' &&
        socialMediaType === 'linkedinPublicPage';

      // No `source` param — these default prompts should show regardless of
      // which LLM (router, ag, etc.) is currently selected, not just
      // whichever one happened to be selected when they were seeded.
      const url = `${BASE_URI}/api/default_prompt_name_history/v5?social_media_name=${shouldUseLinkedIn ? 'LinkedIn' : 'all'
        }&social_media_pills_name=${encodeURIComponent(ChatgptData?.title)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Handle non-2xx responses
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      setPromptApiDataDefalt(responseData);
    } catch (error) {
      // Handle errors
      console.error('Error in Get Request:', error.message);
    }
  };

  const handleGetRequestInititalData = async () => {
    try {
      // Replace 'https://alivesearch.live/api/promptnamehistory/v5' with your actual API endpoint
      // const queryString = new URLSearchParams(newPromptData).toString();
      const url = `${BASE_URI}/api/default_prompt_name_history/v5?social_media_pills_name=${ChatgptData?.title}&social_media_name=${socialMediaType}&source=${sourceData[selectedModel]} 
`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          // Authorization:
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
          // "api-key": process.env.REACT_APP_CHATGPT_API_KEY,
        },
      });

      if (!response.ok) {
        // Handle non-2xx responses
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      // setApiPromptData(responseData);
      // setPromptInitialData([...promptInitialData, ...responseData]);
      // Handle the response data as needed
      setPromptApiData(responseData);
    } catch (error) {
      // Handle errors
      console.error('Error in Get Request:', error.message);
    }
  };

  const handleEditRequest = async () => {
    const updatePromptData = {
      promptName: updateDetails.label || updateDetails.promptName,
      promptValue: prompt,
      chartName: updateDetails.chartName,
      socialMediaName: updateDetails.socialMediaName,
      id: updateDetails.id,
      jobtype: 'promptedit',
    };
    const promptNames = updateDetails.label || updateDetails.promptName;
    try {
      // Replace 'http://172.187.179.39:3002/api/promptnameupdate/v5' with your actual API endpoint
      const response = await fetch(
        //  "https://alivesearch.live/api/edit/v5",
        `${BASE_URI}/api/edit/v5?jobtype=promptedit&promptName=${promptNames}&promptValue=${prompt}&chartName=${updateDetails.chartName}&socialMediaName=${updateDetails.socialMediaName}&id=${updateDetails.id}&source=${sourceData[selectedModel]} `,
        {
          method: 'POST',
          headers: {
            // Authorization:
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.ok) {
        setModalTitle('Update Prompts');
        setshowModalMessage(`Prompt Updated Successfully`);
        setshowModal(true);
        setTimeout(() => setshowModal(false), 2000);
      }
      if (!response.ok) {
        // Handle non-2xx responses
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      // Handle the response data as needed
      setIsClickedAddNew(false);
      setNewPromptName('');
      setNewPromptValue('');
      setUpdateDetails('');
      handleGetRequest();
    } catch (error) {
      // Handle errors
      console.error('Error in Post Request:', error.message);
    }
  };

  const handleDeleteRequest = async () => {
    const updatePromptData = {
      jobtype: 'promptdelete',
      id: updateDetails.id,
    };
    try {
      const response = await fetch(
        `${BASE_URI}/api/remove/v5?id=${updateDetails.id}&jobtype=promptdelete`,
        {
          method: 'POST',
          headers: {
            // Authorization:
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
            // "api-key": process.env.REACT_APP_CHATGPT_API_KEY,
          },
          //  body: JSON.stringify(updatePromptData),
        },
      );
      if (response.ok) {
        setModalTitle('Delete Prompts');
        setshowModalMessage(`Prompt Deleted Successfully`);
        setshowModal(true);
        setTimeout(() => setshowModal(false), 2000);
      }
      if (!response.ok) {
        // Handle non-2xx responses
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      // Handle the response data as needed
      setIsClickedAddNew(false);
      setNewPromptName('');
      setNewPromptValue('');
      setUpdateDetails('');
      handleGetRequest();
    } catch (error) {
      // Handle errors
      console.error('Error in Post Request:', error.message);
    }
  };

  const recognitionRef = useRef(null);
  const [micError, setMicError] = useState('');

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setMicError('Speech recognition not supported. Use Chrome.');
      return;
    }
    const instance = new SR();
    instance.continuous = false;
    instance.interimResults = false;
    instance.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setPrompt((prev) => (prev ? prev + ' ' + transcript : transcript));
      setisMicON(false);
      settoggleMic(false);
      setMicError('');
    };
    instance.onerror = (e) => {
      setisMicON(false);
      settoggleMic(false);
      if (e.error === 'not-allowed') {
        setMicError('Microphone access denied. Allow mic permission in browser settings.');
      } else if (e.error === 'no-speech') {
        setMicError('No speech detected. Try again.');
      } else {
        setMicError(`Mic error: ${e.error}`);
      }
    };
    instance.onend = () => {
      setisMicON(false);
    };
    recognitionRef.current = instance;
  }, []);

  function getuserMicData() {
    if (!recognitionRef.current) {
      setMicError('Speech recognition not supported. Use Chrome.');
      return;
    }
    setMicError('');
    recognitionRef.current.lang = lang || 'en-US';
    setisMicON(true);
    try {
      recognitionRef.current.start();
    } catch (e) {
      setisMicON(false);
      setMicError('Could not start mic. Try again.');
    }
  }

  function StopMic() {
    if (!recognitionRef.current) return;
    setisMicON(false);
    settoggleMic(false);
    recognitionRef.current.stop();
  }

  // function to convert and download pdf
  const convertHtmlToPdf = async () => {
    // Loaded on click rather than statically — jspdf only belongs in the
    // bundle for users who actually export a PDF, not on every page load.
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
    });

    doc.html(document.getElementById('pdf'), {
      callback: () => {
        doc.save('genaireport.pdf');
      },

      x: 0.1,
      y: 0,
      width: 8, //target width in the PDF document
      windowWidth: 650, //window width in CSS pixels
      autoPaging: true,
    });

    setModalTitle('Download PDF');
    setshowModalMessage(`PDF Downloaded Successfully`);
    setshowModal(true);
    setTimeout(() => setshowModal(false), 2000);
  };

  useEffect(() => {
    historyPills();
  }, [selectedDate, selectedPrompt, historyPillsValue]);

  const historyPills = () => {
    if (initialData?.data?.length) {
      // setInitialData(data);
      //
      // ;
      let arr = [];
      let promptPillsArrayTemp = [];
      let pillsHistory = [];
      initialData?.data?.forEach((item) => {
        if (selectedDate != item.date.split('T')[0]) {
          return;
        }

        const obj = {
          id: item.chatId,
          value: item.promptName,
        };

        promptPillsArrayTemp.push(obj);
        if (selectedPrompt && selectedPrompt != item.chatId) {
          return;
        }
        let currentUserMessage = null;
        item.messages.map((message) => {
          if (message.role == 'user') {
            currentUserMessage = { user: message.content, content: null };
          } else if (message.role == 'assistant' && currentUserMessage) {
            currentUserMessage.content = message.content;
            arr.push(currentUserMessage);
            currentUserMessage = null;
          }
        });
      });
      setPromptPillsArray(promptPillsArrayTemp);
      if (selectedDate && selectedPrompt) {
        setSummary(arr);
      }
      // setHIstoryPillsValue(pillsHistory);
      // setresponse(arr); // sets response
      // ShowChat(arr[0]); //displays index 0 data from response.

      setIsLoading(false); // after getting response makes loader false.
    }
  };

  const getColumn = () => {
    if (
      !ChatgptData ||
      !ChatgptData.excelData ||
      ChatgptData.excelData.length === 0
    ) {
      return (
        <div>
          No data found for the selection. Change the search (days etc) for the
          selection. Change the search (days etc).
        </div>
      );
    } else {
      const columnNames = Object.keys(ChatgptData?.excelData?.[0]);

      const options = columnNames?.map((option) => ({
        value: option,
        label: option,
      }));

      setFieldNameDropdown(columnNames);
      setFieldName(options);
    }
  };
  const filterColumns = () => {
    if (
      !ChatgptData ||
      !ChatgptData.excelData ||
      ChatgptData.excelData.length === 0
    ) {
      return null;
    } else {
      const filteredData = Object.values(ChatgptData?.excelData).map(
        (entry) => {
          const filteredEntry = {};
          selectedOptions.forEach((option) => {
            if (option?.value in entry) {
              filteredEntry[option?.value] = entry[option?.value];
            }
          });
          return filteredEntry;
        },
      );
      setFilteredData(filteredData);
    }
  };

  const handleSelectChange = (selectedOptions) => {
    setSelectedOptions(selectedOptions);
  };

  const handleSelectChangeDropdown = (selectedOptions) => {
    setSelectedOptions(selectedOptions);
  };

  useEffect(() => {
    if (
      ChatgptData?.title === 'Posts & Comments/Vision & Text Insights' ||
      ChatgptData?.title === 'AI Video Insights and Viral Posts' ||
      ChatgptData?.title === 'Vision Insights and Mentions (Posts & Comments)' ||
      ChatgptData?.title === 'Vision Insights and Mentions (Posts & Comments) Summary'
    ) {
      const fullSelection = [
        { label: 'Post Date', value: 'Post Date' },
        { label: 'User Name', value: 'User Name' },
        { label: 'User Profile Url', value: 'User Profile Url' },
        { label: 'Engagement', value: 'Engagement' },
        { label: 'Post Url', value: 'Post Url' },
        { label: 'Sentiment (post caption)', value: 'Sentiment (post caption)' },
        { label: 'Post Caption', value: 'Post Caption' },
        { label: 'Comment Text', value: 'Comment Text' },
        { label: 'Comment Url', value: 'Comment Url' },
        { label: 'Social Media', value: 'Social Media' },
        { label: 'User Location', value: 'User Location' },
        { label: 'Named Location', value: 'namedLocation' },
        { label: 'summarized Insights', value: 'summarizedInsights' },
        { label: 'video InsightsIndexer', value: 'videoInsightsIndexer' },
        { label: 'Video Transcript', value: 'videoTrnascript' },
      ];

      const defaultValues = [
        'User Name',
        'User Profile Url',
        'Post Url',
        'Post Caption',
        'Sentiment (post caption)',
        'Engagement',
      ];
      const defaultSelection = fullSelection.filter((f) =>
        defaultValues.includes(f.value),
      );

      setDefaultTitleFields(defaultSelection);
      setFullTitleFields(fullSelection);
      setSelectedOptions(defaultSelection);
    } else if (ChatgptData?.title === 'All Comments') {
      // Different callers populate this "All Comments" excelData with
      // different key shapes (e.g. TopComments.jsx/LinkedIn uses 'Post
      // Date'/'User Name'/'Comment Text'/'Comment Url'; other callers use
      // 'Date'/'username'/'comment_text'/'comment_linkurl'). Hardcoding one
      // shape silently produced {} rows for the other caller — the AI got
      // an array of empty objects with nothing to analyze. Deriving from
      // whatever keys are actually present keeps this correct for both.
      const availableKeys =
        ChatgptData?.excelData?.length > 0
          ? Object.keys(ChatgptData.excelData[0])
          : [];
      const fullSelection = availableKeys.map((k) => ({ label: k, value: k }));
      const preferredDefaults = [
        'Post Date', 'Date',
        'User Name', 'username', 'Username',
        'Comment Text', 'comment_text',
        'Comment Url', 'comment_linkurl', 'Comment URL',
        'Post Url', 'post_linkurl',
      ];
      const preferredSelection = fullSelection.filter((f) =>
        preferredDefaults.includes(f.value),
      );
      const defaultSelection = preferredSelection.length
        ? preferredSelection
        : fullSelection;

      setDefaultTitleFields(defaultSelection);
      setFullTitleFields(fullSelection);
      setSelectedOptions(defaultSelection);
    } else if (ChatgptData?.title == 'Lead Intent') {
      const defaultSelection = [
        { label: 'Date', value: 'Date' },
        // { label: 'Username', value: 'Username' },
        // { label: 'User Profile URL', value: 'User Profile URL' },
        // { label: 'Engagement', value: 'Engagement' },
        { label: 'Post URL', value: 'Post URL' },
        // { label: 'Sentiment Label', value: 'Sentiment Label' },
        { label: 'Post Caption', value: 'Post Caption' },
        // { label: 'Comment Text', value: 'Comment Text' },
        // { label: 'Comment URL', value: 'Comment URL' },
        // { label: 'User Location', value: 'User Location' },

        // { label: 'Named Location', value: 'namedLocation' },
        // { label: 'summarized Insights', value: 'summarizedInsights' },
        // { label: 'video InsightsIndexer', value: 'videoInsightsIndexer' },
        // { label: 'Video Trnascript', value: 'videoTrnascript' },
      ];
      // const defaultSelection = ['Date', 'Post URL', 'Post Caption'];
      setSelectedOptions(defaultSelection);
    } else if (ChatgptData?.isLeadPage) {
      const defaultSelection = [
        { label: 'Date', value: 'Date' },
        // { label: 'Username', value: 'Username' },
        // { label: 'User Profile URL', value: 'User Profile URL' },
        // { label: 'Engagement', value: 'Engagement' },
        { label: 'Post URL', value: 'Post URL' },
        // { label: 'Sentiment Label', value: 'Sentiment Label' },
        { label: 'Post Caption', value: 'Post Caption' },
        // { label: 'Comment Text', value: 'Comment Text' },
        // { label: 'Comment URL', value: 'Comment URL' },
        // { label: 'User Location', value: 'User Location' },

        // { label: 'Named Location', value: 'namedLocation' },
        // { label: 'summarized Insights', value: 'summarizedInsights' },
        // { label: 'video InsightsIndexer', value: 'videoInsightsIndexer' },
        // { label: 'Video Trnascript', value: 'videoTrnascript' },
      ];
      // const defaultSelection = ['Date', 'Post URL', 'Post Caption'];
      setSelectedOptions(defaultSelection);
    } else {
      const arr = [];
      if (
        !ChatgptData ||
        !ChatgptData.excelData ||
        ChatgptData.excelData.length === 0
      ) {
        return [{ value: '', label: '' }];
      } else {
        const columnNames = Object.keys(ChatgptData?.excelData?.[0]);

        const options = columnNames?.map((option) => ({
          value: option,
          label: option,
        }));

        //  setFieldName(options);
        setSelectedOptions(options);
      }
    }
    getColumn();
  }, [ChatgptData]);

  useEffect(() => {
    filterColumns();
  }, [selectedOptions]);

  const CopyTextButton = () => {
    const copyToClipboard = () => {
      navigator.clipboard
        .writeText(summary?.[0]?.content)
        .then(() => {
          // alert("Text copied to clipboard!");
        })
        .catch((error) => {
          console.error('Error copying text: ', error);
        });
    };

    return (
      <button onClick={copyToClipboard} className="copy-button">
        <FaCopy />
      </button>
    );
  };

  // function extractURLAndText(str) {
  //   const urlRegex = /(https?:\/\/\S+)/;
  //   const match = str.match(urlRegex);

  //   if (match) {
  //     const url = match[0];
  //     const text = str.replace(url, '').trim();
  //     return { url, text };
  //   } else {
  //     return { url: null, text: str };
  //   }
  // }

  function extractURLAndText(str) {
    const urlRegex = /(https?:\/\/\S+)/;
    const match = str.match(urlRegex);

    // Normalize line breaks
    let cleanedText = str.replace(/\n{2,}/g, '\n');

    // Convert *text* or **text** into <strong>
    cleanedText = cleanedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    cleanedText = cleanedText.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

    if (match) {
      const url = match[0];
      const text = cleanedText.replace(url, '').trim();
      return { url, text };
    } else {
      return { url: null, text: cleanedText.trim() };
    }
  }
  const [options, setOptions] = useState([]);
  const [selectedPromptTheme, setSelectedPromptTheme] = useState('All');

  useEffect(() => {
    const defaultOptions = promptApiData
      .filter((value) => value.chartName === ChatgptData?.title)
      .map((value) => ({
        value: value.promptValue,
        label: value.promptName,
        type: 'default',
        id: value.id,
        chartName: value.chartName,
        socialMediaName: value.socialMediaName,
      }));

    const defaultOptionsDEfalt = promptApiDataDefalt
      .filter((value) => value.chartName === ChatgptData?.title)
      .map((value) => ({
        value: value.promptValue,
        label: value.promptName,
        type: 'default',
        id: value.id,
        chartName: value.chartName,
        socialMediaName: value.socialMediaName,
        theme: value.prompt_category,
      }));

    let customOptions = [];
    customOptions = apiPromptDataCustom
      ?.filter((value) => value.chartName == ChatgptData?.title)
      .map((value) => ({
        value: value.promptValue,
        label: value.promptName,
        type: 'custom',
        id: value.id,
        chartName: value.chartName,
        socialMediaName: value.socialMediaName,
        theme: value.theme,
      }));
    // "Vision Insights & Mentions" and "All Comments" are the two contexts
    // whose excelData is individual posts/comments (as opposed to pre-
    // aggregated stats) — prompts here can ask the AI to list specific posts,
    // and every post it lists needs its Post/Comment URL so the user can
    // actually click through to it instead of getting an unverifiable claim.
    const POST_URL_TITLES = [
      'Posts & Comments/Vision & Text Insights',
      'AI Video Insights and Viral Posts',
      'Vision Insights and Mentions (Posts & Comments)',
      'Vision Insights and Mentions (Posts & Comments) Summary',
      'All Comments',
    ];
    const wantsPostUrls = POST_URL_TITLES.includes(ChatgptData?.title);
    const postUrlInstruction = wantsPostUrls
      ? ' Whenever you reference a specific post or comment, always include its exact Post URL (or Comment URL) inline next to it — never describe or list a post without linking its URL.'
      : '';

    let defaultPrompt = [
      {
        isActive: true,
        label: 'Overall Summary',
        theme: 'General',
        value:
          'Show summary in bullet points highlighting key issues , trends and dates with variation.If applicable, provide the peak positive & peak negative ' +
          postUrlInstruction,
        type: 'default',
      },
    ];

    // The 20-prompt library (Trends/Post Analysis/Influencers/Competitive
    // Analysis) used to be hardcoded here. It's now DB-backed instead — seeded
    // once through the same promptnameupdate/v5 + promptnamehistory/v5
    // endpoints "+ New prompt" already uses (socialMediaName: ['all'], merged
    // in by handleGetRequest() alongside the current selection's own custom
    // prompts) — so it shows for every user without a code deploy, arriving
    // through `customOptions` above already filtered by chartName.

    let newPromptData = Array.isArray(ChatgptData?.promptOptionData)
      ? ChatgptData.promptOptionData
      : [];

    if (newPromptData && newPromptData.length && newPromptData.length > 0) {
      setOptions([...defaultPrompt, ...defaultOptionsDEfalt, ...newPromptData]);
    } else {
      setOptions([
        ...defaultPrompt,
        ...defaultOptionsDEfalt,
        ...defaultOptions,
        ...customOptions,
      ]);
    }
    setSelectedPromptTheme('All');
  }, [promptApiData, ChatgptData, apiPromptDataCustom]);

  const handleChange = (selectedOption) => {
    if (selectedOption.type === 'default') {
      setTopPillsName(selectedOption.label);
      setPrompt(selectedOption.value);
      setUpdateDetails('');
      setIsClickedAddNew(false);
    } else if (selectedOption.type === 'custom') {
      // handleChangeCustomPrompt(selectedOption);
      setTopPillsName(selectedOption.label);
      setPrompt(selectedOption.value);
      setUpdateDetails(selectedOption);
      setIsClickedAddNew(false);
    } else {
      setTopPillsName(selectedOption.label);
      setPrompt(selectedOption.value);
      setIsClickedAddNew(false);
    }
  };

  useEffect(() => {
    if (SearchObject) {
      calculateDaysDifference(SearchObject?.startDate, SearchObject?.endDate);
    }
  }, [SearchObject]);

  const calculateDaysDifference = (startDate, endDate) => {
    // Parse the dates to ensure they are valid Date objects
    const parsedStartDate = new Date(startDate);
    const parsedEndDate = new Date(endDate);

    // Calculate the time difference in milliseconds
    const timeDifference = parsedEndDate - parsedStartDate;

    // Convert milliseconds to days
    const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    // setDateStartEnd(daysDifference);
    return daysDifference;
  };
  const getDayCount = () => {
    let day;
    if (
      SearchObject?.startDate &&
      SearchObject?.endDate &&
      SearchObject?.updatedDays === 2
    ) {
      day = calculateDaysDifference(
        SearchObject.updateStartDays || SearchObject?.startDate,
        SearchObject.updateEndDays || SearchObject?.endDate,
      );
    } else if (SearchObject?.updateStartDays && SearchObject?.updateEndDays) {
      day = calculateDaysDifference(
        SearchObject.updateStartDays,
        SearchObject.updateEndDays,
      );
    } else if (
      SearchObject?.startDate &&
      SearchObject?.endDate &&
      SearchObject?.updateStartDays === ''
    ) {
      day = calculateDaysDifference(
        SearchObject.startDate,
        SearchObject.endDate,
      );
    } else if (SearchObject?.startDate && SearchObject?.endDate) {
      // Matches CardWidget.jsx's own getDayCount() — some callers (e.g. Top
      // Insights Dashboard KPI cards) populate startDate/endDate without
      // updateStartDays being exactly '' (it's simply undefined), which the
      // branch above missed, silently falling through to a null day count.
      day = calculateDaysDifference(
        SearchObject.updateStartDays || SearchObject.startDate,
        SearchObject.updateEndDays || SearchObject.endDate,
      );
    } else {
      day = SearchObject?.updatedDays
        ? SearchObject?.updatedDays === 2
          ? SearchObject?.days
          : SearchObject?.updatedDays
        : SearchObject?.days || SearchObject?.updatedDateDifference;
    }
    return day;
  };
  const RefreshButton = () => {
    GetAndSetResponse();
  };

  const subscribed_leads_limit = totalAvailableToken;
  const requested_leads_limit = 14;
  const processed_leads_limit = tokenUsed;

  const overlayStyle = {
    backgroundColor: 'transparent',
    backdropFilter: 'none',
    zIndex: 40, // Set the overlay background color to transparent
  };

  const formatNumber = (num) => {
    if (num >= 1_000_000_000_000) {
      return {
        value: (num / 1_000_000_000_000).toFixed(1),
        suffix: 'T',
      };
    } else if (num >= 1_000_000_000) {
      return {
        value: (num / 1_000_000_000).toFixed(1),
        suffix: 'B',
      };
    } else if (num >= 1_000_000) {
      return {
        value: (num / 1_000_000).toFixed(1),
        suffix: 'M',
      };
    } else if (num >= 1_000) {
      return {
        value: (num / 1_000).toFixed(1),
        suffix: 'K',
      };
    } else {
      return {
        value: num,
        suffix: '',
      };
    }
  };

  const { value: formattedValue, suffix } = formatNumber(
    Number(totalAvailableToken),
  );

  // const Progression = (
  //   processed,
  //   subscribed,
  //   requested,
  //   title = '',
  //   description = '',
  //   lastUpdated = '',
  // ) => {
  //   const progressPercentage = () => {
  //     if (!subscribed || !processed || processed <= 0 || subscribed <= 0) {
  //       return '0%';
  //     }

  //     let progress = (processed / subscribed) * 100;
  //     if (progress > 100) {
  //       progress = 100;
  //     }

  //     return Math.round(progress) + '%';
  //   };

  //   return (
  //     <div className="mr-auto w-full pb-4 md:w-[1/2] 2xl:w-[1/3] mb-4 rounded-md overflow-hidden shadow-lg bg-surface">
  //       <div className="px-2 py-2">
  //         <div className="text-center mb-2">
  //           <h2 className="text-md text-left items-center flex font-semibold">
  //             {title}
  //           </h2>
  //           {/* <p className="text-xs text-left text-gray-500">
  //             Last Updated: {lastUpdated}
  //           </p> */}
  //         </div>
  //         <div className="flex flex-col justify-between">
  //           <div className="flex justify-start items-center gap-2 mb-4">
  //             <div className="progress-container bg-primarycontainer rounded-lg ">
  //               <div
  //                 className={`progress-bar ${
  //                   processed && subscribed
  //                     ? processed > subscribed
  //                       ? 'bg-warning'
  //                       : 'bg-primary'
  //                     : 'bg-primary'
  //                 }  h-full rounded-lg`}
  //                 style={{
  //                   width: progressPercentage(),
  //                 }}
  //               ></div>
  //             </div>
  //             <div className="flex flex-col ">
  //               <span className="text-sm text-gray-500">{processed} Used</span>
  //               <span className="text-sm text-gray-500">
  //                 {subscribed + ' '} Total
  //               </span>
  //             </div>
  //           </div>
  //           <div className="flex justify-between">
  //             <button
  //               onClick={() => {
  //                 setSidebarStatus(false);
  //                 navigate('/profile/genaiSubscription');
  //               }}
  //               className={`hover:bg-primary text-sm text-primary font-bold py-1 px-2 rounded ring-1 ${
  //                 processed && subscribed
  //                   ? processed > subscribed
  //                     ? 'ring-warning hover:bg-warning text-warning hover:text-onprimary'
  //                     : 'ring-primary hover:bg-primary hover:text-onprimary opacity-50 hover:opacity-50'
  //                   : 'ring-primary hover:bg-primary hover:text-onprimary opacity-50 hover:opacity-50'
  //               }`}
  //             >
  //               Add more
  //             </button>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  const Progression = (
    processed,
    subscribed,
    requested,
    title = '',
    description = '',
    lastUpdated = '',
  ) => {
    const [isOpen, setIsOpen] = useState(true);
    const navigate = useNavigate();

    const progressPercentage = () => {
      if (!subscribed || !processed || processed <= 0 || subscribed <= 0) {
        return '0%';
      }

      let progress = (processed / subscribed) * 100;
      if (progress > 100) {
        progress = 100;
      }

      return Math.round(progress) + '%';
    };

    return (
      <div className="mr-auto w-full pb-4 md:w-[1/2] 2xl:w-[1/3] mb-4 rounded-md overflow-hidden shadow-lg bg-surface">
        {/* Collapsible Header */}
        <div
          className="flex justify-between items-center px-3 py-2 bg-primarycontainer cursor-pointer select-none"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <h2 className="text-md font-semibold text-left">{title}</h2>
          <span className="text-primary text-lg font-bold">
            {isOpen ? '−' : '+'}
          </span>
        </div>

        {/* Collapsible Body (your original design) */}
        <div
          className={`transition-all duration-500 ease-in-out overflow-hidden ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            }`}
        >
          <div className="px-2 py-2">
            {/* <div className="text-center mb-2">
            <h2 className="text-md text-left items-center flex font-semibold">
              {title}
            </h2> */}
            {/* <p className="text-xs text-left text-gray-500">
              Last Updated: {lastUpdated}
            </p> */}
            {/* </div> */}
            <div className="flex flex-col justify-between">
              <div className="flex justify-start items-center gap-2 mb-4">
                <div className="progress-container bg-primarycontainer rounded-lg ">
                  <div
                    className={`progress-bar ${processed && subscribed
                      ? processed > subscribed
                        ? 'bg-warning'
                        : 'bg-primary'
                      : 'bg-primary'
                      }  h-full rounded-lg`}
                    style={{
                      width: progressPercentage(),
                    }}
                  ></div>
                </div>
                <div className="flex flex-col ">
                  <span className="text-sm text-gray-500">{processed} Used</span>
                  <span className="text-sm text-gray-500">
                    {subscribed + ' '} Total
                  </span>
                </div>
              </div>
              <div className="flex justify-between">
                <button
                  onClick={() => {
                    setSidebarStatus(false);
                    navigate('/profile/genaiSubscription');
                  }}
                  className={`hover:bg-primary text-sm text-primary font-bold py-1 px-2 rounded ring-1 ${processed && subscribed
                    ? processed > subscribed
                      ? 'ring-warning hover:bg-warning text-warning hover:text-onprimary'
                      : 'ring-primary hover:bg-primary hover:text-onprimary opacity-50 hover:opacity-50'
                    : 'ring-primary hover:bg-primary hover:text-onprimary opacity-50 hover:opacity-50'
                    }`}
                >
                  Add more
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 4;

  const handleNext = () => {
    if (currentIndex + itemsPerPage < options.length) {
      setCurrentIndex(currentIndex + itemsPerPage);
    }
  };

  const handlePrev = () => {
    if (currentIndex - itemsPerPage >= 0) {
      setCurrentIndex(currentIndex - itemsPerPage);
    }
  };

  const visibleOptions = options.slice(
    currentIndex,
    currentIndex + itemsPerPage,
  );

  return (
    <div
      id="mySidenavChatgpt"
      style={{
        width: isFullWidth ? '100vw' : result ? result : '60vw',
        maxWidth: isFullWidth ? '100vw' : undefined,
        position: isFullWidth ? 'fixed' : undefined,
        left: isFullWidth ? 0 : undefined,
        top: isFullWidth ? 0 : undefined,
        fontFamily: "'Inter', sans-serif",
      }}
      className={`sidenav !z-[31] ${sidebarStatus ? 'flex' : 'hidden'} flex-col h-screen overflow-hidden bg-white`}
    >

      {/* ── TOP HEADER BAR (full width) ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 text-onprimarycontainer bg-primarycontainer shadow-md">
        <div className="flex flex-col min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight truncate">
            {ChatgptData &&
              mainSearchTerm?.toUpperCase() +
              ': ' +
              (SearchObject.currentMediaChannelMulti &&
                Object.keys(SearchObject.currentMediaChannelMulti).length > 0
                ? Object.keys(SearchObject.currentMediaChannelMulti)
                  .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
                  .join(', ')
                : Array.isArray(socialMediaType) && socialMediaType.length > 0
                  ? socialMediaType.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')
                  : socialMediaType !== undefined
                    ? socialMediaType.charAt(0).toUpperCase() + socialMediaType.slice(1)
                    : '') +
              (ChatgptData.title !== undefined ? ' — "' + ChatgptData.title + '"' : '') +
              ' · ' + getDayCount() + ' days'}
          </p>
          <span className="text-xs text-primary mt-0.5">omniO AI Secured Co-Pilot</span>
          {(() => {
            const dayCount = getDayCount();
            if (!dayCount) return null;
            const { start_date, end_date } = computeDateRangeChatgptApi(SearchObject, dayCount);
            return (
              <span className="text-[11px] text-gray-400 mt-0.5">
                days={dayCount}{start_date && end_date ? ` · ${start_date} → ${end_date}` : ''}
              </span>
            );
          })()}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button title="Disclaimer" onClick={() => setmodal(true)} className="p-1.5 rounded-full hover:bg-white/20 transition">
            <FcDisclaimer className="text-lg" />
          </button>
          <button
            title={isFullWidth ? 'Collapse' : 'Expand to full width'}
            onClick={() => setIsFullWidth(!isFullWidth)}
            className="p-1.5 rounded-full hover:bg-white/20 transition"
          >
            {isFullWidth
              ? <AiOutlineFullscreenExit className="text-base" />
              : <AiOutlineFullscreen className="text-base" />}
          </button>
          <button
            title="Close"
            onClick={() => setSidebarStatus(false)}
            className="p-1.5 rounded-full hover:bg-white/20 transition"
          >
            <AiOutlineClose className="text-base" />
          </button>
        </div>
      </div>

      {/* ── MAIN BODY: LEFT SIDEBAR + RIGHT CHAT AREA ── */}
      <div className="flexy flex flex-1 overflow-hidden">
        {/* ── RESIZE HANDLE (left edge drag) ── */}
        <div id="sliderChatgpt" className="slider" />

        {/* ── LEFT SIDEBAR ── */}
        <div className="w-[220px] flex-shrink-0 flex flex-col bg-[#f7f7f8] border-r border-gray-200 overflow-y-auto">
          {/* LLM Selector */}
          <div className="px-3 pt-4 pb-2">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Select LLM</p>
            <SelectFilterFilter
              value={selectedModel}
              onValueChange={(value) => { setSelectedModel(value); setUserInputShow(false); }}
              options={[
                { value: 'mx_groq', label: 'FastestLLM-M' },
                { value: 'router_model', label: 'Router Model' },
                { value: 'o4_model', label: 'Ollama Mini' },
                { value: 'groq3', label: 'Grok 3' },
                { value: 'llama4_model', label: 'Llama-4' },
                { value: 'llama70b', label: 'Llama-70b' },
                { value: 'openai-azure', label: 'Public LLM' },
                { value: 'llamainstant', label: 'Llama Instant' },
                { value: 'llamaversatile', label: 'Llama Versatile' },
                { value: 'llamadeepseek', label: 'Llama Deepseek' },
                { value: 'mx_groq_llama', label: 'FastestLLM-LongCtx' },
                { value: 'llamaguard', label: 'Llama Guard' },
                { value: 'AdvancedAI', label: 'Secured LLMs - sm' },
                { value: 'mixt-56b', label: 'SecuredLLM-large' },
                { value: 'mixt-large', label: 'Longcontext-56B' },
                { value: 'Claude', label: 'Longcontext-Cl' },
                { value: 'internlm', label: 'largeLLM-in' },
                { value: 'qwen', label: 'largeLLM-qw' },
                { value: 'llama3_gradient', label: 'llama3_gradient' },
                { value: 'llama3_gradient_new', label: 'llama3_gradient_new' },
              ]}
              width="190px"
              placeholder="Select LLM"
            />
          </div>

          {/* Chat History */}
          <div className="px-3 py-2 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Chat history</p>
              <button onClick={RefreshButton} className="text-gray-400 hover:text-gray-600 transition" title="Refresh">
                <span className="material-icons text-sm">refresh</span>
              </button>
            </div>
            {isLoading && (
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-white border border-gray-200 w-fit">
                <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
                <span className="text-[11px] text-gray-500">Fetching history...</span>
              </div>
            )}
            {(() => {
              const periods = [...new Set(historyData.map((h) => h.dayCount).filter(Boolean).map(String))];
              if (periods.length < 2) return null;
              return (
                <div className="flex flex-wrap gap-1 mb-1.5">
                  <button
                    onClick={() => setHistoryPeriodFilter('all')}
                    className={`text-[9px] font-medium rounded-full px-2 py-0.5 transition ${
                      historyPeriodFilter === 'all' ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    All periods
                  </button>
                  {periods.map((p) => (
                    <button
                      key={p}
                      onClick={() => setHistoryPeriodFilter(p)}
                      className={`text-[9px] font-medium rounded-full px-2 py-0.5 transition ${
                        historyPeriodFilter === p ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {p} days
                    </button>
                  ))}
                </div>
              );
            })()}
            <div className="flex flex-col gap-0.5">
              {itemsToShow.map((item, idx) => (
                <div key={idx} className="w-full">
                  <div className="flex items-center justify-between px-1 pt-1">
                    <p className="text-[10px] text-gray-400">{item.date.split('T')[0]}</p>
                    {item.usage?.total_tokens != null && (
                      <span className="text-[9px] font-medium text-indigo-500 bg-indigo-50 rounded-full px-1.5 py-px whitespace-nowrap">
                        {item.usage.total_tokens.toLocaleString()} tok
                      </span>
                    )}
                  </div>
                  {item.dayCount && (
                    <p className="text-[9px] text-gray-400 px-1 truncate">
                      {item.dayCount} days{item.startDate && item.endDate ? ` (${item.startDate} → ${item.endDate})` : ''}
                    </p>
                  )}
                  <button
                    className={`w-full text-left px-2 py-1.5 rounded-md text-xs truncate transition ${selectedPrompt === item.chatId
                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    onClick={() => { setSelectedDate(item.date.split('T')[0]); setSelectedPrompt(item.chatId); }}
                  >
                    {item.chatName || 'Conversation'}
                  </button>
                </div>
              ))}
              {filteredHistoryData.length > 3 && (
                <button onClick={() => setShowAll(!showAll)} className="text-xs text-indigo-500 hover:text-indigo-700 mt-1 px-1">
                  {showAll ? 'Show less' : `+${filteredHistoryData.length - 3} more`}
                </button>
              )}
            </div>
          </div>

          {/* Token Used */}
          <div className="mt-auto px-3 py-3 border-t border-gray-200">
            <div className="bg-indigo-50 rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wider">Token Used</p>
                <button
                  onClick={fetchAccountTokenTotal}
                  disabled={accountTokenLoading}
                  className="text-indigo-400 hover:text-indigo-600 disabled:opacity-40 transition"
                  title="Refresh total token count"
                >
                  <span className={`material-icons text-sm ${accountTokenLoading ? 'animate-spin' : ''}`}>refresh</span>
                </button>
              </div>
              <p className="text-xs text-gray-700 font-semibold">
                {accountTokenTotal != null ? accountTokenTotal.toLocaleString() : '—'} <span className="font-normal text-gray-400">total</span>
              </p>
              {summary.length > 0 && summary[summary.length - 1]?.token && (
                <p className="text-[11px] text-gray-500 mt-0.5">{Number(summary[summary.length - 1].token)?.toLocaleString()} tokens · last prompt</p>
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT CHAT AREA ── */}
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* Source Data Row */}
          <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Select Data Fields</span>
              <button onClick={() => setToggleField(!toggleField)} className="text-gray-400 hover:text-gray-600">
                {toggleField
                  ? <IoIosArrowUp className="text-xs" />
                  : <IoIosArrowDown className="text-xs" />
                }
              </button>
              {fullTitleFields.length > 0 && (() => {
                const hasAll = fullTitleFields.every((f) =>
                  selectedOptions?.some((s) => s.value === f.value),
                );
                return (
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedOptions(hasAll ? defaultTitleFields : fullTitleFields)
                    }
                    className="ml-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-semibold hover:bg-primary/20 transition border border-primary/20"
                  >
                    {hasAll ? 'Fewer fields' : 'More fields'}
                  </button>
                );
              })()}
            </div>
            {toggleField && (
              <Select
                options={fieldName}
                isMulti
                value={selectedOptions}
                onChange={handleSelectChange}
                className="text-xs"
                styles={{ control: (b) => ({ ...b, minHeight: '32px', fontSize: '12px' }) }}
              />
            )}
          </div>

          {/* Prompts Row */}
          <div className="flex-shrink-0 px-4 pt-3 pb-2.5 border-b border-gray-100 bg-white">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold text-primary uppercase tracking-widest bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5">Prompts</span>
              <button onClick={() => setToggleIcon(!toggleIcon)} className="text-gray-300 hover:text-indigo-400 transition">
                {toggleIcon
                  ? <IoIosArrowUp className="text-xs" />
                  : <IoIosArrowDown className="text-xs" />
                }
              </button>
              <div className="flex-1" />
              <button
                title="Add new prompt"
                className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-semibold hover:bg-primary/20 transition border border-primary/20"
                onClick={() => { setIsClickedAddNew(!isClickedAddNew); setNewPromptValue(prompt); setUpdateDetails(''); }}
              >
                <svg width={10} height={10} fill="none" strokeWidth={2.5} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                New prompt
              </button>
            </div>
            {toggleIcon && (() => {
              // Themes only show up when the option list actually has more
              // than one (i.e. the 20-prompt library for post/comment
              // contexts) — other contexts just keep the flat pill row.
              const themes = [...new Set(options.map((o) => o.theme).filter(Boolean))]
                .filter((t) => t !== 'General');
              const visibleOptions = themes.length > 0
                ? options.filter((o) => selectedPromptTheme === 'All' || o.theme === selectedPromptTheme || o.theme === 'General')
                : options;
              const themeEmoji = {
                Trends: '🔥',
                'Post Analysis': '📝',
                Influencers: '🧑‍🤝‍🧑',
                'Competitive Analysis': '⚔️',
              };
              const selectedPillClass =
                'bg-gradient-to-r from-indigo-600 to-blue-500 text-white shadow-md shadow-indigo-200';
              const unselectedPillClass =
                'bg-indigo-50 text-indigo-600 hover:bg-indigo-100';
              return (
                <>
                  {themes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      <button
                        onClick={() => setSelectedPromptTheme('All')}
                        className={`text-[11px] font-semibold rounded-full px-2.5 py-1 transition ${
                          selectedPromptTheme === 'All' ? selectedPillClass : unselectedPillClass
                        }`}
                      >
                        ✨ All themes
                      </button>
                      {themes.map((t) => (
                        <button
                          key={t}
                          onClick={() => setSelectedPromptTheme(t)}
                          className={`text-[11px] font-semibold rounded-full px-2.5 py-1 transition ${
                            selectedPromptTheme === t ? selectedPillClass : unselectedPillClass
                          }`}
                        >
                          {themeEmoji[t] ? `${themeEmoji[t]} ` : ''}{t}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {visibleOptions.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleChange(item)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition shadow-sm ${topPillsName === item.label
                          ? 'bg-primary text-onprimary shadow-md'
                          : 'bg-white text-gray-600 border border-gray-200 hover:border-primary/40 hover:text-primary hover:shadow'
                          }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Chat Messages Area */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50" ref={scrollContainerRef}>
            {summary.length === 0 && !isLoading && !isAiLoading && (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center mb-4 shadow-lg border border-gray-100 p-2">
                  <img
                    src="/avatars/HAIXforwebsite.png"
                    className="max-h-10 w-auto object-contain"
                    alt="omniO"
                  />
                </div>
                <p className="text-gray-700 font-semibold text-base">Ask omniO AI anything</p>
                <p className="text-gray-400 text-sm mt-1">Select a prompt above or type your question below</p>
              </div>
            )}
            {[...summary].reverse().map((line, index) => (
              <div key={index} className="space-y-3">
                {/* User bubble */}
                <div className="flex justify-end">
                  <div className="max-w-[75%] bg-black text-white rounded-2xl rounded-tr-none px-4 py-2.5 text-sm shadow-sm">
                    {line?.user}
                  </div>
                </div>
                {/* AI bubble */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow">
                    <img src="/avatars/HAIXforwebsite.png" className="h-5 w-auto" alt="logo" />
                  </div>
                  <div className="flex-1 bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm border border-gray-100 text-sm text-gray-800 leading-relaxed">
                    {(line?.token || line?.dayCount) && (
                      <div className="flex flex-wrap items-center gap-1.5 mb-2">
                        {line?.token && (
                          <span className="inline-block text-[10px] text-indigo-400 font-medium bg-indigo-50 px-2 py-0.5 rounded-full">
                            {Number(line.token)?.toLocaleString()} tokens
                          </span>
                        )}
                        {line?.dayCount && (
                          <span className="inline-block text-[10px] text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded-full">
                            {line.dayCount} days{line.startDate && line.endDate ? ` (${line.startDate} → ${line.endDate})` : ''}
                          </span>
                        )}
                      </div>
                    )}
                    {line?.content?.split('\n').map((item, i) => {
                      const { url, text } = extractURLAndText(item.trim());
                      return (
                        <div key={i}>
                          {url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-500 underline">{item}</a>
                          ) : (
                            <p dangerouslySetInnerHTML={{ __html: text }} />
                          )}
                        </div>
                      );
                    })}
                    {/* Action buttons */}
                    <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
                      <button
                        title="Delete"
                        onClick={() => setSummary((prev) => prev.filter((_, i) => i !== (summary.length - 1 - index)))}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
                      >
                        <AiFillDelete className="text-sm" />
                      </button>
                      <button
                        title="Download PDF"
                        onClick={convertHtmlToPdf}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition"
                      >
                        <FaFileDownload className="text-sm" />
                      </button>
                      <button
                        title="Copy"
                        onClick={() => navigator.clipboard.writeText(line?.content || '')}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition"
                      >
                        <FaCopy className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {isAiLoading && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <img src="/avatars/HAIXforwebsite.png" className="h-5 w-auto" alt="logo" />
                </div>
                <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm border border-gray-100 flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-xs text-gray-400">Generating insights…</span>
                </div>
              </div>
            )}
            <div id="scrollIntoView" ref={messagesEndRef} />
          </div>

          {/* ── STICKY INPUT AREA ── */}
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3">
            {/* API key / instruction inputs (conditional) */}
            {userInputShow && (
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  className="flex-1 py-1.5 px-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  placeholder="Enter API key"
                  value={apikeyValue}
                  onChange={(e) => setApiKey(e.target.value)}
                />
                <input
                  type="text"
                  className="flex-1 py-1.5 px-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  placeholder="System prompt instructions"
                  value={selectInstruction}
                  onChange={(e) => setSelectInstruction(e.target.value)}
                />
              </div>
            )}

            {/* Add new prompt form */}
            {isClickedAddNew && (
              <div className="mb-3 rounded-2xl overflow-hidden shadow-sm border border-indigo-100 bg-white">
                <div className="px-4 py-2.5 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
                  <span className="text-xs font-semibold text-indigo-600 tracking-wide">Save as new prompt</span>
                  <button
                    onClick={() => { setIsClickedAddNew(false); setNewPromptName(''); setNewPromptValue(''); setUpdateDetails(''); }}
                    className="text-indigo-300 hover:text-indigo-600 transition text-base leading-none"
                  >✕</button>
                </div>
                <div className="p-3 space-y-2">
                  <input
                    type="text"
                    className="w-full py-2 px-3 rounded-xl bg-gray-50 border-0 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    placeholder="Prompt name…"
                    value={newPromptName}
                    onChange={(e) => setNewPromptName(e.target.value)}
                  />
                  <textarea
                    className="w-full min-h-[72px] py-2 px-3 rounded-xl bg-gray-50 border-0 text-xs text-gray-700 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    placeholder="Prompt content…"
                    value={newPromptValue}
                    onChange={(e) => setNewPromptValue(e.target.value)}
                  />
                  <div className="flex justify-end gap-2 pt-1">
                    {updateDetails === '' && (
                      <button
                        onClick={handlePostRequest}
                        disabled={!newPromptName?.trim() || !newPromptValue?.trim()}
                        className="px-4 py-1.5 bg-primary text-white text-xs font-medium rounded-xl hover:bg-indigo-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
                      >
                        Save
                      </button>
                    )}
                    {updateDetails !== '' && (
                      <>
                        <button onClick={handleEditRequest} className="px-4 py-1.5 bg-primary text-white text-xs font-medium rounded-xl hover:bg-indigo-700 transition shadow-sm">Update</button>
                        <button onClick={handleDeleteRequest} className="px-4 py-1.5 bg-red-500 text-white text-xs font-medium rounded-xl hover:bg-red-600 transition shadow-sm">Delete</button>
                      </>
                    )}
                    <button
                      onClick={() => { setIsClickedAddNew(false); setNewPromptName(''); setNewPromptValue(''); setUpdateDetails(''); }}
                      className="px-4 py-1.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-xl hover:bg-gray-200 transition "
                    >Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Main textarea + send */}
            {!isClickedAddNew && (
              <div className="rounded-2xl bg-white shadow-md">
                {isMicON && (
                  <div className="flex items-center gap-2 px-4 pt-2 text-xs text-red-500 font-medium animate-pulse">
                    <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
                    Listening… speak now
                  </div>
                )}
                {micError && (
                  <div className="px-4 pt-2 text-xs text-red-500">{micError}</div>
                )}
                <textarea
                  className="w-full bg-transparent resize-none text-sm text-gray-800 placeholder-gray-400 outline-none focus:outline-none ring-0 focus:ring-0 border-0 px-4 pt-3 pb-1 min-h-[52px] max-h-[160px]"
                  placeholder="Type a prompt or select one above…"
                  name="prompt"
                  alt="Prompt_field"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (isLoading || isAiLoading) return;
                      handleFeedBtn();
                      setToggleIcon(false);
                    }
                  }}
                  rows={2}
                />
                {/* Bottom toolbar */}
                <div className="flex items-center gap-2 px-3 pb-2.5 pt-1">
                  <button
                    title={isMicON ? 'Stop recording' : 'Voice input'}
                    onClick={() => {
                      if (!recognitionRef.current) {
                        alert('Speech recognition is not supported in this browser. Please use Chrome.');
                        return;
                      }
                      if (isMicON) {
                        StopMic();
                      } else {
                        settoggleMic(true);
                        getuserMicData();
                      }
                    }}
                    className={`w-7 h-7 rounded-full flex items-center justify-center transition ${
                      isMicON
                        ? 'text-red-500 bg-red-50 animate-pulse'
                        : 'text-gray-400 hover:text-indigo-500 hover:bg-indigo-50'
                    }`}
                  >
                    <BsFillMicFill className="text-sm" />
                  </button>
                  <select
                    className="text-xs bg-gray-50 border-0 rounded-lg px-2 py-1 text-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                    onChange={(e) => setlang(e.target.value)}
                  >
                    <option value="">Language</option>
                    {LANGUAGES_LIST.map((item) => (
                      <option key={item.code} value={item.language}>{item.language}</option>
                    ))}
                  </select>
                  <div className="flex-1" />
                  <button
                    onClick={() => { handleFeedBtn(); setToggleIcon(false); }}
                    disabled={(ChatgptData != null && !isPromptData) || isLoading || isAiLoading}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition shadow-sm ${(ChatgptData != null && !isPromptData) || isLoading || isAiLoading
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary text-white hover:opacity-90'
                      }`}
                    title="Show Insights"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M13 6l6 6-6 6" /></svg>
                  </button>
                </div>
              </div>
            )}
            {selectedModel === 'mx_groq' && timer !== 30 && timer > 1 && (
              <p className="text-xs text-amber-500 mt-1.5 text-center">⏱ Please wait {timer}s before next request…</p>
            )}
          </div>

        </div>
      </div>

      {/* ── HIDDEN PDF DIV ── */}
      <div className="hidden">
        <div id="pdf" className="px-6 pt-1 pb-6 overflow-y-scroll !text-sm">
          <div className="mt-3 flex justify-start px-2 items-center">
            <img src="/avatars/HAIXforwebsite.png" className="ml-2 h-auto w-24" alt="logo" />
          </div>
          <div className="text-sm flex justify-start items-center">Powered by omniO AI</div>
          <div className="flex justify-end text-sm items-center px-2">Date: {dayjs(new Date().getTime()).format('DD/MMM/YYYY')}</div>
          <div className="text-[20px] text-green-700 border-b py-4 text-center font-semibold capitalize">
            {`"omniO AI" Analysis for ${label}` || 'Welcome To omniO AI'}
          </div>
          <div className="mt-4 mb-6 text-xs px-2">
            <span className="font-bold">omniO AI Analysis Context</span>: This is short summary for {mainSearchTerm} on {socialMediaType} data for {ChatgptData?.title} data for {SearchObject?.days} days using {topPillsName}. For full customized report, reach our Customer Success team at <span>team@omniosocial.com</span>.
          </div>
          {summary.reverse().map((line, index) => (
            <div key={index} className="border-b-2 my-2">
              <div className="my-1 bg-green-100">
                <div className="font-bold">User Question:</div>
                <div>{line?.user}</div>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="font-bold">Answer by omniO AI Assistant: </span>
                <div className="flex items-center gap-2">
                  {line?.cached && (
                    <span className="text-xs font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      Saved{line?.savedAt ? ` · ${new Date(line.savedAt).toLocaleString()}` : ''}
                    </span>
                  )}
                  {line?.cached && index === 0 && (
                    <button
                      type="button"
                      onClick={handleRegenerate}
                      disabled={isLoading || isAiLoading}
                      className="text-xs text-primary hover:underline disabled:opacity-50"
                      title="Run this question again instead of reusing the saved answer"
                    >
                      Regenerate
                    </button>
                  )}
                </div>
              </div>
              {line?.content?.split('\n').map((item, i) => {
                const { url, text } = extractURLAndText(item.trim());
                return (
                  <div key={i}>
                    {url ? <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary">{item}</a> : <p>{text}</p>}
                    <br />
                  </div>
                );
              })}
            </div>
          ))}
          <div className="my-2 py-2 text-xs px-2">
            <span className="font-bold">Disclaimer</span>: Please be aware that this complex social media analysis is conducted using AI and large language models, which may not always be highly accurate. It might contain personal data; therefore, adhere to GDPR policies when using the data. Use your discretion in decision-making.
          </div>
        </div>
      </div>

      {/* ── DIALOGS ── */}
      <Dialog
        showDialog={modal}
        positiveButton1={{ text: 'Close', callback: () => setmodal(false) }}
        setShowDialog={setmodal}
        title="Disclaimer"
        body="Please be aware that this complex social media analysis is conducted using AI and large language models, which may not always be highly accurate. It might contain personal data; therefore, adhere to GDPR policies when using the data. Use your discretion in decision-making."
      />
      {showModal && (
        <Dialog
          title={ModalTitle}
          body={showModalMessage}
          positiveButton1={{ text: 'Ok', callback: () => { } }}
          showDialog={showModal}
          setShowDialog={setshowModal}
        />
      )}
      {showModalSubscribe && (
        <Dialog
          title={ModalTitle}
          body={showModalMessage}
          positiveButton1={{ text: 'Subscribe', callback: () => { navigate('/'); } }}
          showDialog={showModalSubscribe}
          setShowDialog={setShowModalSubscribe}
          negativeButton2={{ text: 'Cancel', callback: () => { } }}
        />
      )}
    </div>
  );


};

export default memo(Chatgpt_API);

{
  /* <div className="w-[85%] h-max rounded bg-gray-200 p-3 text-[14px]">
<p className="text-[16px] w-full ">
  Secured & Private Generative AI Model
</p>
<p>
  Important Note: Please be aware that the data passing to the
  model might be truncated or cut short due to the input tokens
  limit. This could impact the model&apos;s performance and
  accuracy, so result&apos;s might not be as expected.
</p>
</div>

<div className="w-[85%] h-max rounded bg-gray-200 p-3 text-[14px]">
<p>
  Tip: Pro Tip: Enhance model performance by incorporating
  essential keywords and phrases such as &ldquo;provide a detailed
  summary of the following JSON data&ldquo; in your prompts. These
  cues can lead to more accurate and comprehensive results from
  the AI model.
</p>
</div> */
}
